import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import * as XLSX from 'xlsx';

// ── Constants ────────────────────────────────────────────────────────────────

// AP's submitted rates (from their spreadsheet — these are MINIMUMS to beat)
const AP_RATES = {
  sorterSunday:  57.71,
  sorterWeekday: 39.76,
  tlSunday:      62.28,
  tlWeekday:     42.90,
};

// Default rates — pre-loaded with AP's figures; Paul updates after PACT verification
const DEFAULT_INPUTS = {
  // PACT-verified rates (editable)
  sorterSunday:  57.71,
  sorterWeekday: 39.76,
  tlSunday:      62.28,
  tlWeekday:     42.90,
  superRate:     0.12,
  wcRate:        0.032,
  // Break treatment
  sundayBreakPaid: true,  // AP treats 30-min Sunday break as billable — our position
  weekdayBreakPaid: true, // 15-min weekday break explicitly noted as paid in AP schedule
  // Management loading — now split Admin + Profit (AP asked us to break it down).
  // markupPct is the total loading; profitPct is the profit slice; admin is the rest.
  markupPct: 0.15,   // 10% admin + 5% profit
  profitPct: 0.05,   // AP's guideline ceiling for profit
  // Admin role: the task list + operating systems below JUSTIFY the admin fee
  // (they document the scope; the fee itself is adminPct = markup − profit).
  adminRate: 55,      // fully-loaded coordinator / management hourly rate (indicative)
  adminWeeks: 52,
  adminTasks: [
    { label: 'Shift allocation & rostering (3 chutes, 5 nights)', hrs: 2.0 },
    { label: 'Nightly shift confirmation', hrs: 2.5 },
    { label: 'Shift adjustments & short-notice absence cover', hrs: 2.0 },
    { label: 'Pre-shift briefings & toolbox talks', hrs: 1.0 },
    { label: 'Timesheet verification & payroll / STP prep', hrs: 2.5 },
    { label: 'Recruitment, onboarding & training', hrs: 1.5 },
    { label: 'Discipline & performance management', hrs: 1.0 },
    { label: 'Award compliance & record-keeping', hrs: 1.0 },
    { label: 'WHS & incident reporting', hrs: 0.5 },
  ],
  opex: [
    { label: 'Deputy — rostering & time-and-attendance', amount: 4200 },
    { label: 'Xero — payroll, STP & accounting', amount: 1320 },
    { label: 'iComply — compliance & chain-of-responsibility', amount: 1800 },
    { label: 'Recruitment & advertising (casual turnover)', amount: 3000 },
    { label: 'Mobile, IT & Google Workspace', amount: 2400 },
    { label: 'Public liability & professional indemnity', amount: 3600 },
  ],
  // Negotiation scenarios (editable on the page)
  negOpening:    0.15, // start here (10% admin + 5% profit)
  negCompromise: 0.135, // mid-point if AP pushes back
  negWalkaway:   0.12, // do not accept below this
  apAnnualSpend: 878697.08, // AP's TRUE 12-month gross spend ex GST (13 PVA invoices 18 May 25 – 16 May 26, before parcel clawback)
  // Annual weeks
  sundayWeeks: 52,
  weekdayWeeks: 208, // 4 weekday shifts x 52 weeks
};

// Schedule data
const CHUTES = [
  {
    id: '005', name: 'City', rank: 1,
    sorters: 3, runners: 2, tl: 1,
    sundayStart: '23:15', sundayEnd: '06:15',
    weekdayStart: '23:45', weekdayEnd: '06:15',
    sundayBreakMins: 30, weekdayBreakMins: 15,
    // AP's billed hours (break included)
    apSundayHours: 7, apWeekdayHours: 6.5,
  },
  {
    id: '006', name: 'Darlinghurst', rank: 2,
    sorters: 3, runners: 0, tl: 0,
    sundayStart: '23:15', sundayEnd: '06:15',
    weekdayStart: '23:45', weekdayEnd: '06:15',
    sundayBreakMins: 30, weekdayBreakMins: 15,
    apSundayHours: 7, apWeekdayHours: 6.5,
  },
  {
    id: '015', name: 'Broadway', rank: 3,
    sorters: 1, runners: 0, tl: 0,
    sundayStart: '23:15', sundayEnd: '06:15',
    weekdayStart: '23:45', weekdayEnd: '06:15',
    sundayBreakMins: 30, weekdayBreakMins: 15,
    apSundayHours: 7, apWeekdayHours: 6.5,
  },
];

const STORE_KEY = 'hp-nightsort-inputs';

// True PVA invoice values — N22000 City Night Sort, ex GST, taken from the 13
// tax invoices covering 18 May 2025 – 16 May 2026 (52 weeks). `gross` is the
// TRUE invoice value BEFORE AP's per-parcel clawback (−$0.2868/parcel), which
// applied on invoices 545–557 and ceased from invoice 560 (Oct 2025).
const AP_INVOICES = [
  { inv: '545', period: '18 May – 14 Jun 25', gross: 73063.90, clawback: 39385.67 },
  { inv: '548', period: '15 Jun – 12 Jul 25', gross: 73878.99, clawback: 41205.70 },
  { inv: '551', period: '13 Jul – 10 Aug 25', gross: 62677.23, clawback: 36270.16 },
  { inv: '554', period: '10 Aug – 6 Sep 25',  gross: 60873.49, clawback: 39809.28 },
  { inv: '557', period: '7 Sep – 4 Oct 25',   gross: 60784.06, clawback: 38394.20 },
  { inv: '560', period: '5 Oct – 1 Nov 25',   gross: 58227.88, clawback: 0 },
  { inv: '563', period: '2 Nov – 29 Nov 25',  gross: 85132.24, clawback: 0 },
  { inv: '566', period: '30 Nov – 27 Dec 25', gross: 90643.45, clawback: 0 },
  { inv: '569', period: '21 Dec – 24 Jan 26', gross: 75466.65, clawback: 0 },
  { inv: '572', period: '25 Jan – 21 Feb 26', gross: 60044.21, clawback: 0 },
  { inv: '574', period: '22 Feb – 21 Mar 26', gross: 61214.00, clawback: 0 },
  { inv: '580', period: '22 Mar – 19 Apr 26', gross: 57004.47, clawback: 0 },
  { inv: '583', period: '19 Apr – 16 May 26', gross: 59686.51, clawback: 0 },
];
const AP_TRUE_GROSS   = AP_INVOICES.reduce((s, i) => s + i.gross, 0);          // 878,697.08
const AP_CLAWBACK_TOT = AP_INVOICES.reduce((s, i) => s + i.clawback, 0);       // 195,065.01
// Post-clawback run-rate: invoices 560–583 cover 28 weeks — annualise to 52.
const AP_RUN_RATE = AP_INVOICES.slice(5).reduce((s, i) => s + i.gross, 0) * 52 / 28;

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeToHours(start, end) {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let s = sh * 60 + sm;
  let e = eh * 60 + em;
  if (e <= s) e += 24 * 60; // crosses midnight
  return (e - s) / 60;
}

function calcChute(chute, inp) {
  const totalSundayHrs = timeToHours(chute.sundayStart, chute.sundayEnd);
  const totalWeekdayHrs = timeToHours(chute.weekdayStart, chute.weekdayEnd);

  const sundayBillable = inp.sundayBreakPaid
    ? totalSundayHrs
    : totalSundayHrs - chute.sundayBreakMins / 60;
  const weekdayBillable = inp.weekdayBreakPaid
    ? totalWeekdayHrs
    : totalWeekdayHrs - chute.weekdayBreakMins / 60;

  const workers = chute.sorters + chute.runners;

  const sundayLab  = workers * sundayBillable  * inp.sorterSunday;
  const weekdayLab = workers * weekdayBillable * inp.sorterWeekday;

  // TL only on chute 005
  const tlSundayLab  = chute.tl * sundayBillable  * inp.tlSunday;
  const tlWeekdayLab = chute.tl * weekdayBillable * inp.tlWeekday;

  const sundayTotal  = sundayLab  + tlSundayLab;
  const weekdayTotal = weekdayLab + tlWeekdayLab;

  // AP figures for comparison
  const apSundayTotalLab  = workers * chute.apSundayHours  * AP_RATES.sorterSunday
                          + chute.tl * chute.apSundayHours  * AP_RATES.tlSunday;
  const apWeekdayTotalLab = workers * chute.apWeekdayHours * AP_RATES.sorterWeekday
                          + chute.tl * chute.apWeekdayHours * AP_RATES.tlWeekday;

  return {
    sundayBillable, weekdayBillable, workers,
    sundayLab, weekdayLab, tlSundayLab, tlWeekdayLab,
    sundayTotal, weekdayTotal,
    apSundayTotalLab, apWeekdayTotalLab,
    perWorkerSundayHrs: sundayBillable,
    perWorkerWeekdayHrs: weekdayBillable,
    totalWeeklyLabour: sundayTotal + weekdayTotal * 4,
    apWeeklyLabour: apSundayTotalLab + apWeekdayTotalLab * 4,
  };
}

function calcAnnual(chutes, inp) {
  const sundayWeeklyAll  = chutes.reduce((s, c) => s + calcChute(c, inp).sundayTotal,  0);
  const weekdayWeeklyAll = chutes.reduce((s, c) => s + calcChute(c, inp).weekdayTotal, 0);
  const weeklyLabour = sundayWeeklyAll + weekdayWeeklyAll * 4;
  const annualLabour = sundayWeeklyAll * inp.sundayWeeks + weekdayWeeklyAll * inp.weekdayWeeks;
  const annualSuper  = annualLabour * inp.superRate;
  const annualWC     = annualLabour * inp.wcRate;
  const annualTotal  = annualLabour + annualSuper + annualWC;
  const annualMarkup = annualTotal * inp.markupPct;
  const annualPrice  = annualTotal + annualMarkup;

  // ── Loading split — Admin fee + Profit (AP asked us to break the loading down) ──
  // Profit is a fixed % of the cost base; Admin is the remainder of the loading.
  // The task list + operating systems justify the admin fee but no longer drive the
  // price. adminHeadroom = the gap between the admin fee and the demonstrable admin
  // cost — internal negotiation room, never shown to AP.
  const profitPct         = inp.profitPct ?? 0.05;
  const profit            = annualTotal * profitPct;
  const adminPct          = inp.markupPct - profitPct;
  const adminFee          = annualMarkup - profit;            // = annualTotal * adminPct
  const adminHoursPerWeek = (inp.adminTasks || []).reduce((s, t) => s + (parseFloat(t.hrs) || 0), 0);
  const adminWeeks        = inp.adminWeeks ?? 52;
  const adminAnnualHours  = adminHoursPerWeek * adminWeeks;
  const adminLabour       = adminAnnualHours * (parseFloat(inp.adminRate) || 0);
  const opexTotal         = (inp.opex || []).reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
  const adminDemonstrable = adminLabour + opexTotal;
  const adminHeadroom     = adminFee - adminDemonstrable;

  // AP annual (from their spreadsheet)
  const apSundayDaily  = chutes.reduce((s, c) => s + calcChute(c, inp).apSundayTotalLab,  0);
  const apWeekdayDaily = chutes.reduce((s, c) => s + calcChute(c, inp).apWeekdayTotalLab, 0);
  const apWeeklyLabour = apSundayDaily + apWeekdayDaily * 4;
  const apAnnualLabour = apSundayDaily * 52 + apWeekdayDaily * 208;
  const apAnnualSuper  = apAnnualLabour * 0.12;
  const apAnnualWC     = apAnnualLabour * 0.032;
  const apAnnualTotal  = apAnnualLabour + apAnnualSuper + apAnnualWC;

  return {
    weeklyLabour, annualLabour, annualSuper, annualWC, annualTotal,
    annualMarkup, annualPrice,
    profitPct, profit, adminPct, adminFee,
    adminHoursPerWeek, adminWeeks, adminAnnualHours, adminLabour, opexTotal,
    adminDemonstrable, adminHeadroom,
    apWeeklyLabour, apAnnualLabour, apAnnualSuper, apAnnualWC, apAnnualTotal,
  };
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmt$(n, dec = 2) {
  if (n == null || isNaN(n)) return '$0.00';
  const abs = Math.abs(n).toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function fmtHrs(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm === 0 ? `${hh}h` : `${hh}h ${mm}m`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Card({ children, style }) {
  return (
    <div style={{
      background: 'var(--hp-surface)', border: '1px solid var(--hp-border)',
      borderRadius: 10, padding: '20px 24px', ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--hp-text)' }}>{children}</div>
      {sub && <div style={{ color: 'var(--hp-dim)', fontSize: 12, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function InfoBadge({ label, value, accent = '#3b82f6' }) {
  return (
    <div style={{
      background: 'var(--hp-bg)', border: `1px solid var(--hp-border)`,
      borderLeft: `4px solid ${accent}`, borderRadius: 8,
      padding: '12px 16px', minWidth: 140,
    }}>
      <div style={{ fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--hp-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function CompareRow({ label, our, ap, highlight }) {
  const diff = our - ap;
  const diffColor = diff > 0 ? '#ef4444' : diff < 0 ? '#22c55e' : 'var(--hp-dim)';
  return (
    <tr style={{ background: highlight ? 'rgba(234,179,8,0.07)' : 'transparent' }}>
      <td style={{ padding: '8px 0', color: 'var(--hp-text)', fontSize: 14 }}>{label}</td>
      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--hp-text)' }}>{fmt$(our)}</td>
      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ap)}</td>
      <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700, color: diffColor, fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
        {diff !== 0 ? (diff > 0 ? '+' : '') + fmt$(diff) : '—'}
      </td>
    </tr>
  );
}

function RateInput({ label, value, onChange, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: 'var(--hp-dim)', fontWeight: 700 }}>$</span>
        <input
          type="number" step="0.01" min="0" value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          style={{
            width: 90, padding: '6px 10px', borderRadius: 6,
            border: '1px solid var(--hp-border)', background: 'var(--hp-bg)',
            color: '#2563eb', fontWeight: 700, fontSize: 15, fontVariantNumeric: 'tabular-nums',
            fontFamily: 'var(--font-body)',
          }}
        />
        <span style={{ fontSize: 11, color: 'var(--hp-dim)' }}>/hr</span>
      </div>
      {note && <div style={{ fontSize: 11, color: 'var(--hp-dim)' }}>{note}</div>}
    </div>
  );
}

// ── Break Analysis Card ───────────────────────────────────────────────────────

function BreakAnalysisCard({ inp, onChange }) {
  const sundayBreakHrs = 30 / 60;
  const weekdayBreakHrs = 15 / 60;

  // Total workers across all chutes on Sunday (no double-counting TL)
  const totalSundayWorkers = CHUTES.reduce((s, c) => s + c.sorters + c.runners, 0) + 1; // +1 TL
  const totalWeekdayWorkers = CHUTES.reduce((s, c) => s + c.sorters + c.runners, 0) + 1;

  const avgSundayRate  = AP_RATES.sorterSunday; // rough
  const avgWeekdayRate = AP_RATES.sorterWeekday;

  const sundayBreakImpact  = totalSundayWorkers  * sundayBreakHrs  * avgSundayRate  * 52;
  const weekdayBreakImpact = totalWeekdayWorkers * weekdayBreakHrs * avgWeekdayRate * 208;

  return (
    <Card>
      <SectionTitle sub="Under RDTA MA000038 — do breaks count as billable hours?">
        Break Time Analysis
      </SectionTitle>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Sunday break */}
        <div style={{
          border: `2px solid ${inp.sundayBreakPaid ? '#22c55e' : '#ef4444'}`,
          borderRadius: 8, padding: '14px 16px',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Sunday — 30 min break</span>
            <span style={{ fontSize: 12, color: inp.sundayBreakPaid ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
              {inp.sundayBreakPaid ? 'PAID' : 'UNPAID'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--hp-dim)', marginBottom: 10, lineHeight: 1.5 }}>
            AP bills <strong>7.0 hrs</strong> (23:15–06:15) — full shift including the 30-min rest break.
            They do NOT note this break as paid, unlike the weekday 15-min breaks.
          </div>
          <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 10, lineHeight: 1.5, background: 'rgba(245,158,11,0.08)', padding: '8px 10px', borderRadius: 6 }}>
            <strong>Our view:</strong> Under clause 22.3 of the RDTA, a 30-min meal break on a 7-hour shift
            is ordinarily <em>unpaid</em> unless the worker is required to remain on post.
            AP billing for this break is likely deliberate — annual exposure ≈ {fmt$(sundayBreakImpact, 0)}.
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={inp.sundayBreakPaid}
              onChange={e => onChange('sundayBreakPaid', e.target.checked)} />
            Include 30-min break in billable hours (match AP)
          </label>
        </div>

        {/* Weekday break */}
        <div style={{
          border: `2px solid ${inp.weekdayBreakPaid ? '#22c55e' : '#ef4444'}`,
          borderRadius: 8, padding: '14px 16px',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Mon–Thu — 15 min break</span>
            <span style={{ fontSize: 12, color: inp.weekdayBreakPaid ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
              {inp.weekdayBreakPaid ? 'PAID' : 'UNPAID'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--hp-dim)', marginBottom: 10, lineHeight: 1.5 }}>
            AP bills <strong>6.5 hrs</strong> (23:45–06:15) including the 15-min break,
            which is explicitly noted as "paid" in their schedule. This is consistent and correct.
          </div>
          <div style={{ fontSize: 12, color: '#22c55e', marginBottom: 10, lineHeight: 1.5, background: 'rgba(34,197,94,0.08)', padding: '8px 10px', borderRadius: 6 }}>
            <strong>Our view:</strong> 15-min paid rest break on a 6.5-hour shift is
            standard under clause 22.1. AP's treatment here is <em>correct</em>.
            We should match this in our pricing.
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={inp.weekdayBreakPaid}
              onChange={e => onChange('weekdayBreakPaid', e.target.checked)} />
            Include 15-min break in billable hours (correct — match AP)
          </label>
        </div>
      </div>

      <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
        <strong style={{ color: '#22c55e' }}>What AP allows (our pricing basis):</strong> AP bills the full shift
        including all breaks — 7 hrs Sunday-start (30-min break in), 6.5 hrs Mon–Thu (15-min break in). We believe
        this is deliberate: by paying penalties on the full shift with breaks included, AP can never be accused of
        underpayment. We price on AP's own treatment. The strict-award value of the Sunday break alone is{' '}
        <strong>{fmt$(sundayBreakImpact, 0)}/yr</strong> — that is private margin headroom, not a dispute to raise.
      </div>
    </Card>
  );
}

// ── Rate Verification Card ────────────────────────────────────────────────────

function RatesCard({ inp, onChange }) {
  return (
    <Card>
      <SectionTitle sub="Enter PACT calculator results below — RDTA MA000038, Grade 1 casual">
        Pay Rate Verification
      </SectionTitle>

      <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
        <strong>PACT Calculator Action:</strong> Go to Fair Work Ombudsman → Pay Calculator → Road Transport
        Distribution Award 2020 [MA000038] → Grade 1 → Casual → apply the following:
        <ul style={{ margin: '8px 0 0 16px', paddingLeft: 0 }}>
          <li><strong>Casual loading:</strong> 25% (confirm)</li>
          <li><strong>Early morning / night shift penalty:</strong> verify % (we believe 30%)</li>
          <li><strong>Sunday penalty:</strong> 200% ordinary rate (confirm applies to full shift when shift starts Sunday)</li>
          <li><strong>Team Leader grade:</strong> A TL supervising 10–15 persons is likely <strong>Grade 3 or Grade 4</strong> under RDTA.
            Check the classification definitions — Grade 4 (Foreperson) oversees a work group. Confirm with PACT.</li>
        </ul>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 16, marginBottom: 16 }}>
        <RateInput label="Sorter — Sunday" value={inp.sorterSunday}
          onChange={v => onChange('sorterSunday', v)}
          note={`AP uses $${AP_RATES.sorterSunday}/hr`} />
        <RateInput label="Sorter — Weekday" value={inp.sorterWeekday}
          onChange={v => onChange('sorterWeekday', v)}
          note={`AP uses $${AP_RATES.sorterWeekday}/hr`} />
        <RateInput label="Team Leader — Sunday" value={inp.tlSunday}
          onChange={v => onChange('tlSunday', v)}
          note={`AP uses $${AP_RATES.tlSunday}/hr`} />
        <RateInput label="Team Leader — Weekday" value={inp.tlWeekday}
          onChange={v => onChange('tlWeekday', v)}
          note={`AP uses $${AP_RATES.tlWeekday}/hr`} />
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Super rate</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" step="0.01" min="0" max="1" value={(inp.superRate * 100).toFixed(1)}
              onChange={e => onChange('superRate', (parseFloat(e.target.value) || 0) / 100)}
              style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: '#2563eb', fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-body)' }} />
            <span style={{ fontSize: 13, color: 'var(--hp-dim)' }}>%</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Work Cover</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" step="0.01" min="0" max="100" value={(inp.wcRate * 100).toFixed(1)}
              onChange={e => onChange('wcRate', (parseFloat(e.target.value) || 0) / 100)}
              style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: '#2563eb', fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-body)' }} />
            <span style={{ fontSize: 13, color: 'var(--hp-dim)' }}>%</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Our Markup</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" step="0.5" min="0" max="100" value={(inp.markupPct * 100).toFixed(1)}
              onChange={e => onChange('markupPct', (parseFloat(e.target.value) || 0) / 100)}
              style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: '#2563eb', fontWeight: 700, fontSize: 15, fontFamily: 'var(--font-body)' }} />
            <span style={{ fontSize: 13, color: 'var(--hp-dim)' }}>%</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Chute Detail Tab ──────────────────────────────────────────────────────────

function ChuteTab({ chute, inp }) {
  const c = calcChute(chute, inp);
  const totalHrs = chute.sorters + chute.runners;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <SectionTitle sub={`${chute.name} operations — RDTA Grade 1 Casual`}>
          Chute {chute.id} — {chute.name}
        </SectionTitle>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 100%), 1fr))', gap: 12, marginBottom: 20 }}>
          <InfoBadge label="Sorters" value={chute.sorters} accent="#8b5cf6" />
          <InfoBadge label="Runners" value={chute.runners} accent="#06b6d4" />
          {chute.tl > 0 && <InfoBadge label="Team Leader" value={chute.tl} accent="#f59e0b" />}
          <InfoBadge label="Sunday shift" value={`${chute.sundayStart}–${chute.sundayEnd}`} accent="#ef4444" />
          <InfoBadge label="Weekday shift" value={`${chute.weekdayStart}–${chute.weekdayEnd}`} accent="#3b82f6" />
          <InfoBadge label="Sunday billable" value={fmtHrs(c.sundayBillable)} accent="#ef4444" />
          <InfoBadge label="Weekday billable" value={fmtHrs(c.weekdayBillable)} accent="#3b82f6" />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Component</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Our Rate</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Hours</th>
                <th style={{ textAlign: 'right', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Our Cost</th>
                <th style={{ textAlign: 'right', padding: '8px 0 8px 12px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>AP Cost</th>
              </tr>
            </thead>
            <tbody>
              {/* Sunday */}
              <tr style={{ borderTop: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '10px 0', fontWeight: 700, color: '#ef4444', fontSize: 13 }} colSpan={5}>Sunday</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0 6px 16px', color: 'var(--hp-text)' }}>
                  Sorters + Runners ({c.workers} × {fmtHrs(c.sundayBillable)})
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inp.sorterSunday)}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(c.workers * c.sundayBillable).toFixed(2)}h</td>
                <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt$(c.sundayLab)}</td>
                <td style={{ padding: '6px 0 6px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.workers * chute.apSundayHours * AP_RATES.sorterSunday)}</td>
              </tr>
              {chute.tl > 0 && (
                <tr>
                  <td style={{ padding: '6px 0 6px 16px', color: 'var(--hp-text)' }}>
                    Team Leader (1 × {fmtHrs(c.sundayBillable)})
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inp.tlSunday)}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.sundayBillable.toFixed(2)}h</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt$(c.tlSundayLab)}</td>
                  <td style={{ padding: '6px 0 6px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(chute.apSundayHours * AP_RATES.tlSunday)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '1px solid var(--hp-border)', background: 'rgba(239,68,68,0.04)' }}>
                <td style={{ padding: '8px 0 8px 16px', fontWeight: 700 }}>Sunday Daily Total</td>
                <td colSpan={2} />
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 800, color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.sundayTotal)}</td>
                <td style={{ padding: '8px 0 8px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.apSundayTotalLab)}</td>
              </tr>

              {/* Weekday */}
              <tr style={{ borderTop: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '10px 0', fontWeight: 700, color: '#3b82f6', fontSize: 13 }} colSpan={5}>Mon–Thu (each day)</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0 6px 16px', color: 'var(--hp-text)' }}>
                  Sorters + Runners ({c.workers} × {fmtHrs(c.weekdayBillable)})
                </td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inp.sorterWeekday)}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(c.workers * c.weekdayBillable).toFixed(2)}h</td>
                <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt$(c.weekdayLab)}</td>
                <td style={{ padding: '6px 0 6px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.workers * chute.apWeekdayHours * AP_RATES.sorterWeekday)}</td>
              </tr>
              {chute.tl > 0 && (
                <tr>
                  <td style={{ padding: '6px 0 6px 16px', color: 'var(--hp-text)' }}>
                    Team Leader (1 × {fmtHrs(c.weekdayBillable)})
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inp.tlWeekday)}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.weekdayBillable.toFixed(2)}h</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt$(c.tlWeekdayLab)}</td>
                  <td style={{ padding: '6px 0 6px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(chute.apWeekdayHours * AP_RATES.tlWeekday)}</td>
                </tr>
              )}
              <tr style={{ borderTop: '1px solid var(--hp-border)', background: 'rgba(59,130,246,0.04)' }}>
                <td style={{ padding: '8px 0 8px 16px', fontWeight: 700 }}>Weekday Daily Total</td>
                <td colSpan={2} />
                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 800, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.weekdayTotal)}</td>
                <td style={{ padding: '8px 0 8px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.apWeekdayTotalLab)}</td>
              </tr>

              {/* Weekly */}
              <tr style={{ borderTop: '2px solid var(--hp-border)' }}>
                <td style={{ padding: '10px 0', fontWeight: 800 }}>
                  Weekly Labour (1× Sun + 4× Weekday)
                </td>
                <td colSpan={2} />
                <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 800, fontSize: 16, color: 'var(--hp-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.totalWeeklyLabour)}</td>
                <td style={{ padding: '10px 0 10px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--hp-dim)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.apWeeklyLabour)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Shift notes — AP-allowed treatment (pricing basis) vs award-strict actual */}
      <Card style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--hp-dim)' }}>
        <SectionTitle>Award Notes — Chute {chute.id}</SectionTitle>

        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
          <strong style={{ color: '#22c55e' }}>What AP allows — our pricing basis:</strong> the full Sunday-start
          shift (23:15–06:15) is billed at Sunday penalty rates, and all breaks are included as paid time. AP pays
          this deliberately — penalties on the whole shift with breaks in means they can never be accused of
          underpaying. Our price adopts AP's own treatment, so every dollar is defensible against their figures.
        </div>

        <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 12 }}>
          <strong style={{ color: '#f59e0b' }}>Award-strict position (actual — MA000038 shiftwork clause):</strong>{' '}
          <em>"Shiftworkers, for work on a rostered shift, the major portion of which is performed on a Saturday,
          Sunday or public holiday will be paid… Sunday — at the rate of double time."</em> It is an all-or-nothing
          majority test per shift — not a whole-shift-by-start-day rule and not a midnight split. Our Sunday-start
          shift has only 45 minutes on the Sunday; the major portion falls on Monday, so strictly the entire shift
          is an ordinary weekday night shift (130% + 25% casual). Strict-award labour is ≈ $66k/yr below AP's
          allowance (≈ $91k at price including on-costs and margin). <strong>Confidential</strong> — this is
          negotiation headroom; never raise it with AP, as it argues their price down.
        </div>

        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li><strong>Night shift loading:</strong> 130% of the ordinary rate for night shift (a shift finishing
            after midnight and at or before 8am) — verify via PACT.</li>
          <li><strong>Casual loading:</strong> 25% on top, paid in lieu of leave entitlements (AP's rates are
            internally consistent with night 130% + 25% weekdays and Sunday 200% + 25%).</li>
          <li><strong>Breaks:</strong> AP includes all breaks as billable on every shift — matched in our pricing
            (see Break Time Analysis on the Summary tab).</li>
        </ul>
      </Card>
    </div>
  );
}

// ── Management Loading Breakdown ──────────────────────────────────────────────
// Splits the management loading into Admin & management + Profit (AP asked us to
// break the old "contingency" word down). Profit is a fixed % of cost; admin is
// the rest of the loading, justified by the task list + operating systems below.

function LoadingBreakdownCard({ inp, onChange }) {
  const ann   = calcAnnual(CHUTES, inp);
  const tasks = inp.adminTasks || [];
  const opex  = inp.opex || [];

  const setTasks = arr => onChange('adminTasks', arr);
  const setOpex  = arr => onChange('opex', arr);
  const updTask  = (i, patch) => setTasks(tasks.map((t, j) => j === i ? { ...t, ...patch } : t));
  const addTask  = () => setTasks([...tasks, { label: 'New task', hrs: 0.5 }]);
  const delTask  = i => setTasks(tasks.filter((_, j) => j !== i));
  const updOpex  = (i, patch) => setOpex(opex.map((o, j) => j === i ? { ...o, ...patch } : o));
  const addOpex  = () => setOpex([...opex, { label: 'New expense', amount: 0 }]);
  const delOpex  = i => setOpex(opex.filter((_, j) => j !== i));

  const loading   = ann.annualMarkup || 0;
  const pctOf     = n => loading > 0 ? (n / loading * 100) : 0;
  const adminNeg  = ann.adminFee < 0;
  const SEGMENTS = [
    { key: 'admin',  label: `Admin & management (${(ann.adminPct * 100).toFixed(1).replace(/\.0$/, '')}%)`, val: ann.adminFee, color: '#3b82f6' },
    { key: 'profit', label: `Profit (${(ann.profitPct * 100).toFixed(1).replace(/\.0$/, '')}%)`,             val: ann.profit,   color: '#22c55e' },
  ];

  const inputBox = { background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 6, color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13, padding: '5px 8px' };

  return (
    <Card style={{ borderLeft: '4px solid #22c55e' }}>
      <SectionTitle sub="What the management loading funds — Admin & management + Profit (use this when AP asks what the loading covers)">
        Management Loading — {(inp.markupPct * 100).toFixed(0)}% ({(ann.adminPct * 100).toFixed(0)}% Admin + {(ann.profitPct * 100).toFixed(0)}% Profit)
      </SectionTitle>

      {/* The spiel — no "contingency" */}
      <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '16px 18px', marginBottom: 18, fontSize: 14, lineHeight: 1.8, color: 'var(--hp-text)' }}>
        <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 15 }}>What to say to Australia Post:</div>
        <p style={{ margin: '0 0 10px' }}>
          "We have reduced our loading to {(inp.markupPct * 100).toFixed(0)}% and split it as you asked. A
          {' '}<strong>{(ann.adminPct * 100).toFixed(0)}% admin &amp; management fee</strong> ({fmt$(ann.adminFee, 0)}/yr)
          covers the dedicated administration of the operation — shift allocation and rostering, nightly shift confirmation,
          short-notice absence cover, briefings, timesheet and payroll processing, recruitment and discipline, award
          compliance, and the operating systems behind it (Deputy, Xero, iComply, IT) — a management role of about
          {' '}{ann.adminHoursPerWeek.toFixed(0)} hours every week. The remaining
          {' '}<strong>{(ann.profitPct * 100).toFixed(0)}% is profit</strong> ({fmt$(ann.profit, 0)}/yr), in line with your guideline."
        </p>
        <p style={{ margin: 0, color: 'var(--hp-dim)', fontSize: 13 }}>
          Base award rates, superannuation (12%) and workers' compensation (3.2%) are fixed pass-through costs. Variable,
          one-off items — overtime and ad-hoc absence cover beyond the agreed roster — are <strong>not</strong> built into
          the rate; they would be billed as PVAs only if and when they occur, so the contract price reflects the agreed
          specification and nothing speculative."
        </p>
      </div>

      {/* Two-way split + stacked bar */}
      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 10 }}>
        {SEGMENTS.map(s => (
          <div key={s.key} style={{ background: 'var(--hp-bg)', border: `1px solid ${s.color}55`, borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: s.color, marginRight: 6 }} />
              {s.label}
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.key === 'admin' && adminNeg ? '#ef4444' : 'var(--hp-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(s.val, 0)}</div>
            <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>{(s.val / (ann.annualPrice || 1) * 100).toFixed(1)}% of contract price</div>
            {s.key === 'profit' && (
              <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 6 }}>
                Profit %{' '}
                <input type="number" step="0.5" min="0" max="100" value={(ann.profitPct * 100).toFixed(1).replace(/\.0$/, '')}
                  onChange={e => onChange('profitPct', (parseFloat(e.target.value) || 0) / 100)}
                  style={{ ...inputBox, width: 56, textAlign: 'right' }} /> of cost
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', height: 12, borderRadius: 99, overflow: 'hidden', marginBottom: 6 }}>
        {SEGMENTS.map(s => (
          <div key={s.key} title={`${s.label}: ${fmt$(s.val, 0)}`} style={{ width: `${Math.max(0, pctOf(s.val))}%`, background: s.color }} />
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginBottom: 18 }}>
        Admin + profit = the {(inp.markupPct * 100).toFixed(0)}% loading ({fmt$(loading, 0)}). Set the loading on the Summary tab; profit % above sets the split.
        {adminNeg && <span style={{ color: '#ef4444', fontWeight: 700 }}> — ⚠ Profit % exceeds the loading; lower profit or raise the loading.</span>}
      </div>

      {/* Internal headroom note — NOT for AP */}
      <div style={{ fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.6, padding: '10px 14px', background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.3)', borderRadius: 8, marginBottom: 18 }}>
        <strong style={{ color: 'var(--hp-gold)' }}>Internal only:</strong> demonstrable admin cost ≈ {fmt$(ann.adminDemonstrable, 0)}
        {' '}(management labour {fmt$(ann.adminLabour, 0)} + operating systems {fmt$(ann.opexTotal, 0)}). The {(ann.adminPct * 100).toFixed(0)}% admin fee
        therefore carries ≈ {fmt$(ann.adminHeadroom, 0)} of headroom — that's your concession room if AP pushes, on top of the {(ann.profitPct * 100).toFixed(0)}% profit. Do not show this to AP.
      </div>

      {/* Admin role — task → hours (justification / scope) */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Admin role — sections &amp; hours (justifies the fee)
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--hp-text)' }}>
            <strong>{ann.adminHoursPerWeek.toFixed(1)} h/wk</strong> · {ann.adminAnnualHours.toLocaleString()} h/yr
            {' '}@ <input type="number" step="1" min="0" value={inp.adminRate}
              onChange={e => onChange('adminRate', parseFloat(e.target.value) || 0)}
              style={{ ...inputBox, width: 56, textAlign: 'right' }} />/h
            {' '}≈ <strong style={{ color: '#3b82f6' }}>{fmt$(ann.adminLabour, 0)}</strong>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {tasks.map((t, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '5px 0' }}>
                  <input value={t.label} onChange={e => updTask(i, { label: e.target.value })} style={{ ...inputBox, width: '100%', border: 'none', background: 'transparent', padding: '3px 0' }} />
                </td>
                <td style={{ padding: '5px 0', textAlign: 'right', width: 90 }}>
                  <input type="number" step="0.25" min="0" value={t.hrs} onChange={e => updTask(i, { hrs: parseFloat(e.target.value) || 0 })} style={{ ...inputBox, width: 64, textAlign: 'right' }} />
                  <span style={{ color: 'var(--hp-dim)', fontSize: 11, marginLeft: 4 }}>h</span>
                </td>
                <td style={{ padding: '5px 0', textAlign: 'right', width: 24 }}>
                  <button onClick={() => delTask(i)} aria-label="Remove" style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 15 }}>×</button>
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: 800 }}>
              <td style={{ padding: '8px 0' }}>Total admin hours / week</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ann.adminHoursPerWeek.toFixed(1)} h</td>
              <td />
            </tr>
          </tbody>
        </table>
        <button onClick={addTask} style={{ marginTop: 6, padding: '5px 11px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 7, color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'var(--font-body)' }}>+ Add task</button>
      </div>

      {/* Operating systems the admin fee covers */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Operating systems the admin fee covers
          </div>
          <div style={{ fontSize: 12.5 }}><strong style={{ color: '#f59e0b' }}>{fmt$(ann.opexTotal, 0)}</strong>/yr</div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {opex.map((o, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '5px 0' }}>
                  <input value={o.label} onChange={e => updOpex(i, { label: e.target.value })} style={{ ...inputBox, width: '100%', border: 'none', background: 'transparent', padding: '3px 0' }} />
                </td>
                <td style={{ padding: '5px 0', textAlign: 'right', width: 120 }}>
                  <span style={{ color: 'var(--hp-dim)' }}>$</span>
                  <input type="number" step="100" min="0" value={o.amount} onChange={e => updOpex(i, { amount: parseFloat(e.target.value) || 0 })} style={{ ...inputBox, width: 88, textAlign: 'right' }} />
                </td>
                <td style={{ padding: '5px 0', textAlign: 'right', width: 24 }}>
                  <button onClick={() => delOpex(i)} aria-label="Remove" style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 15 }}>×</button>
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: 800 }}>
              <td style={{ padding: '8px 0' }}>Total operating systems</td>
              <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.opexTotal, 0)}</td>
              <td />
            </tr>
          </tbody>
        </table>
        <button onClick={addOpex} style={{ marginTop: 6, padding: '5px 11px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 7, color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'var(--font-body)' }}>+ Add system</button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.6, padding: '10px 14px', background: 'var(--hp-bg)', borderRadius: 8, border: '1px solid var(--hp-border)' }}>
        <strong>Industry benchmark:</strong> managed casual staffing typically carries 20–35% all-in; specialist overnight 25–35%.
        At {(inp.markupPct*100).toFixed(0)}% all-in (with profit at just {(ann.profitPct*100).toFixed(0)}%), Hollypaul sits <strong>well below</strong> the market range.
      </div>
    </Card>
  );
}

// ── Negotiation Range ─────────────────────────────────────────────────────────

function NegotiationRangeCard({ inp, onChangeInp }) {
  // Editable scenarios — percentages live in inp (persisted) so Paul can adjust them.
  const SCENARIOS = [
    { key: 'negWalkaway',   pct: inp.negWalkaway   ?? 0.16, tag: 'Walk-away minimum', tagColor: '#ef4444', desc: 'Do not accept below this' },
    { key: 'negCompromise', pct: inp.negCompromise ?? 0.18, tag: 'Compromise',        tagColor: '#f59e0b', desc: 'Mid-point if AP pushes back' },
    { key: 'negOpening',    pct: inp.negOpening    ?? 0.20, tag: 'Opening position',  tagColor: '#22c55e', desc: 'Start here — justified & defensible' },
  ];

  // AP's current 12-month spend — editable so the comparison can be kept current.
  const AP_ANNUAL = inp.apAnnualSpend ?? 878697.08;

  function priceAtMarkup(pct) {
    const c005 = calcChute(CHUTES[0], inp);
    const c006 = calcChute(CHUTES[1], inp);
    const c015 = calcChute(CHUTES[2], inp);
    const sundayWeekly  = c005.sundayTotal  + c006.sundayTotal  + c015.sundayTotal;
    const weekdayWeekly = c005.weekdayTotal + c006.weekdayTotal + c015.weekdayTotal;
    const annualLabour  = sundayWeekly * inp.sundayWeeks + weekdayWeekly * inp.weekdayWeeks;
    const annualTotal   = annualLabour * (1 + inp.superRate + inp.wcRate);
    return annualTotal * (1 + pct);
  }

  return (
    <Card>
      <SectionTitle sub="Model your negotiation — edit any scenario %, then click a card to make it the active markup">
        Negotiation Range
      </SectionTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
        {SCENARIOS.map(s => {
          const price    = priceAtMarkup(s.pct);
          const vsAP     = price - AP_ANNUAL;
          const isActive = Math.abs(inp.markupPct - s.pct) < 0.001;
          return (
            <div key={s.key}
              onClick={() => onChangeInp('markupPct', s.pct)}
              style={{ textAlign: 'left', padding: '16px 18px', borderRadius: 10, border: `2px solid ${isActive ? s.tagColor : 'var(--hp-border)'}`, background: isActive ? s.tagColor + '10' : 'var(--hp-bg)', cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'all 0.15s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                {/* Editable % — clicking/typing here must not trigger the card's set-active */}
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="number" step="0.5" min="0" max="100"
                    value={(s.pct * 100).toFixed(1).replace(/\.0$/, '')}
                    onChange={e => {
                      const v = (parseFloat(e.target.value) || 0) / 100;
                      onChangeInp(s.key, v);
                      if (isActive) onChangeInp('markupPct', v); // keep active pricing in sync
                    }}
                    style={{ width: 64, fontSize: 24, fontWeight: 900, color: isActive ? s.tagColor : 'var(--hp-text)', background: 'transparent', border: 'none', borderBottom: `2px dashed ${s.tagColor}55`, padding: 0, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}
                  />
                  <span style={{ fontSize: 18, fontWeight: 900, color: isActive ? s.tagColor : 'var(--hp-dim)' }}>%</span>
                </span>
                <span style={{ padding: '2px 10px', borderRadius: 99, background: s.tagColor + '20', color: s.tagColor, fontSize: 11, fontWeight: 700 }}>{s.tag}</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--hp-text)', fontVariantNumeric: 'tabular-nums', marginBottom: 4 }}>
                {fmt$(price, 0)}
              </div>
              <div style={{ fontSize: 12, marginBottom: 6, fontVariantNumeric: 'tabular-nums',
                color: vsAP > 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                {vsAP > 0 ? '▲' : '▼'} {fmt$(Math.abs(vsAP), 0)} {vsAP > 0 ? 'more' : 'less'} than AP's current spend
              </div>
              <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>{s.desc}</div>
              <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
                Weekly RCTI: {fmt$(price / 52, 0)} · incl GST: {fmt$(price / 52 * 1.1, 0)}
              </div>
              {isActive && (
                <div style={{ marginTop: 8, fontSize: 11, color: s.tagColor, fontWeight: 700 }}>
                  ✓ Currently active
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>AP's current 12-month spend on PVA 805909:</span>
        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span style={{ color: 'var(--hp-text)', fontWeight: 700 }}>$</span>
          <input
            type="number" step="1000" min="0" value={Math.round(AP_ANNUAL)}
            onChange={e => onChangeInp('apAnnualSpend', parseFloat(e.target.value) || 0)}
            style={{ width: 110, color: 'var(--hp-text)', fontWeight: 700, background: 'transparent', border: 'none', borderBottom: '1px dashed var(--hp-border)', padding: 0, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
          />
        </span>
        <span>(incl. overtime) · Edit any scenario % above, or click a card to set the active markup.</span>
      </div>
    </Card>
  );
}

// ── Summary Tab ───────────────────────────────────────────────────────────────

function SummaryTab({ inp, onChangeInp }) {
  const ann = calcAnnual(CHUTES, inp);
  const chute005 = calcChute(CHUTES[0], inp);
  const chute006 = calcChute(CHUTES[1], inp);
  const chute015 = calcChute(CHUTES[2], inp);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Top KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 12 }}>
        <InfoBadge label="Weekly Labour" value={fmt$(ann.weeklyLabour, 0)} accent="#3b82f6" />
        <InfoBadge label="Annual Labour" value={fmt$(ann.annualLabour, 0)} accent="#8b5cf6" />
        <InfoBadge label="Annual + Super + WC" value={fmt$(ann.annualTotal, 0)} accent="#f59e0b" />
        <InfoBadge label={`Our Price (${(inp.markupPct*100).toFixed(0)}% markup)`} value={fmt$(ann.annualPrice, 0)} accent="#22c55e" />
        <InfoBadge label="AP Annual Total" value={fmt$(ann.apAnnualTotal, 0)} accent="#6b7280" />
      </div>

      {/* ── vs AP comparison ── */}
      <Card>
        <SectionTitle sub="Our pricing vs Australia Post's submitted figures">
          Pricing Comparison
        </SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Item</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#2563eb', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Our Calc</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>AP Submitted</th>
                <th style={{ textAlign: 'right', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Diff</th>
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Annual Labour (pre super/WC)" our={ann.annualLabour} ap={ann.apAnnualLabour} />
              <CompareRow label="Superannuation (12%)" our={ann.annualSuper} ap={ann.apAnnualSuper} />
              <CompareRow label="Work Cover (3.2%)" our={ann.annualWC} ap={ann.apAnnualWC} />
              <CompareRow label="Total Cost per annum" our={ann.annualTotal} ap={ann.apAnnualTotal} highlight />
              <CompareRow label={`Our price incl ${(inp.markupPct*100).toFixed(0)}% markup`} our={ann.annualPrice} ap={ann.apAnnualTotal} highlight />
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Per-chute weekly breakdown ── */}
      <Card>
        <SectionTitle sub="Weekly labour cost per chute (before super/WC)">
          Chute Breakdown — Weekly
        </SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Chute</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Staff</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#ef4444', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Sunday</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', color: '#3b82f6', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Per Weekday</th>
                <th style={{ textAlign: 'right', padding: '8px 0', color: 'var(--hp-dim)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>Weekly Total</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: '005 — City', c: chute005, ch: CHUTES[0] },
                { label: '006 — Darlinghurst', c: chute006, ch: CHUTES[1] },
                { label: '015 — Broadway', c: chute015, ch: CHUTES[2] },
              ].map(({ label, c, ch }) => (
                <tr key={label} style={{ borderTop: '1px solid var(--hp-border)' }}>
                  <td style={{ padding: '10px 0', fontWeight: 600 }}>{label}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--hp-dim)' }}>
                    {ch.sorters}S {ch.runners > 0 ? `${ch.runners}R` : ''} {ch.tl > 0 ? '1TL' : ''}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.sundayTotal)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3b82f6', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.weekdayTotal)}</td>
                  <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmt$(c.totalWeeklyLabour)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--hp-border)', fontWeight: 800 }}>
                <td style={{ padding: '10px 0' }}>TOTAL</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--hp-dim)', fontWeight: 600 }}>
                  {CHUTES.reduce((s,c)=>s+c.sorters+c.runners,0)}S+R, 1TL
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444', fontVariantNumeric: 'tabular-nums' }}>{fmt$(chute005.sundayTotal + chute006.sundayTotal + chute015.sundayTotal)}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{fmt$(chute005.weekdayTotal + chute006.weekdayTotal + chute015.weekdayTotal)}</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.weeklyLabour)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Negotiation Range ── */}
      <NegotiationRangeCard inp={inp} onChangeInp={onChangeInp} />

      {/* ── Management Loading Breakdown ── */}
      <LoadingBreakdownCard inp={inp} onChange={onChangeInp} />

      {/* ── Annual pricing build-up ── */}
      <Card>
        <SectionTitle sub="Contract pricing build-up for submission to Australia Post">
          Annual Contract Price
        </SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Sunday labour × {inp.sundayWeeks} weeks</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$((chute005.sundayTotal+chute006.sundayTotal+chute015.sundayTotal)*inp.sundayWeeks)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Weekday labour × {inp.weekdayWeeks} shifts</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$((chute005.weekdayTotal+chute006.weekdayTotal+chute015.weekdayTotal)*inp.weekdayWeeks)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)', fontWeight: 700 }}>
                <td style={{ padding: '8px 0' }}>Total Annual Labour</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualLabour)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Superannuation ({(inp.superRate*100).toFixed(0)}%)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualSuper)}</td>
              </tr>
              <tr style={{ borderBottom: '2px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Work Cover ({(inp.wcRate*100).toFixed(1)}%)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualWC)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)', fontWeight: 700 }}>
                <td style={{ padding: '10px 0' }}>Total Cost</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualTotal)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Admin &amp; management ({(ann.adminPct*100).toFixed(0)}% · {ann.adminHoursPerWeek.toFixed(1)} h/wk)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.adminFee)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--hp-border)' }}>
                <td style={{ padding: '8px 0', color: 'var(--hp-dim)' }}>Profit ({(ann.profitPct*100).toFixed(0)}%)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: ann.profit < 0 ? '#ef4444' : 'inherit' }}>{fmt$(ann.profit)}</td>
              </tr>
              <tr style={{ borderBottom: '2px solid var(--hp-border)', fontWeight: 700 }}>
                <td style={{ padding: '8px 0' }}>Management loading ({(inp.markupPct*100).toFixed(0)}%)</td>
                <td style={{ padding: '8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualMarkup)}</td>
              </tr>
              <tr>
                <td style={{ padding: '14px 0', fontWeight: 800, fontSize: 18, color: '#22c55e' }}>CONTRACT PRICE (ex GST)</td>
                <td style={{ padding: '14px 0', textAlign: 'right', fontWeight: 800, fontSize: 18, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualPrice)}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--hp-dim)', fontSize: 13 }}>Weekly RCTI line item (÷52)</td>
                <td style={{ padding: '6px 0', textAlign: 'right', color: 'var(--hp-dim)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualPrice / 52)}</td>
              </tr>
              <tr>
                <td style={{ padding: '6px 0', color: 'var(--hp-dim)', fontSize: 13 }}>Weekly RCTI incl GST</td>
                <td style={{ padding: '6px 0', textAlign: 'right', color: 'var(--hp-dim)', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{fmt$(ann.annualPrice / 52 * 1.1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, fontSize: 13, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
          <strong>Tax Invoice rule:</strong> When a sorter works beyond the contracted hours on any day,
          generate a Tax Invoice to Australia Post for the additional time at the applicable
          hourly rate (Sunday or weekday rate as appropriate), plus GST.
        </div>
      </Card>

      {/* ── AP historical payments — true PVA invoice values ── */}
      <Card>
        <SectionTitle sub="PVA — N22000 City Night Sort · 13 tax invoices · 18 May 2025 – 16 May 2026 (52 weeks) · TRUE invoice values ex GST, before AP's per-parcel clawback">
          AP Historical Payments — True Invoice Values
        </SectionTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {AP_INVOICES.map(i => (
            <div key={i.inv} style={{ background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 14px', minWidth: 138, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, marginBottom: 3 }}>Inv {i.inv} · {i.period}</div>
              <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>{fmt$(i.gross, 0)}</div>
              {i.clawback > 0 && (
                <div style={{ fontSize: 10.5, color: '#ef4444', marginTop: 2 }}>incl {fmt$(i.clawback, 0)} clawed back</div>
              )}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontWeight: 800, fontSize: 15, color: 'var(--hp-text)' }}>
          True gross (52 weeks): {fmt$(AP_TRUE_GROSS)}{' '}
          <span style={{ color: 'var(--hp-dim)', fontWeight: 400, fontSize: 13 }}>
            ex GST, incl overtime · AP per-parcel clawback {fmt$(AP_CLAWBACK_TOT)} added back (net paid {fmt$(AP_TRUE_GROSS - AP_CLAWBACK_TOT)})
          </span>
        </div>
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
          The −$0.2868/parcel clawback applied on invoices 545–557 and <strong>ceased from invoice 560 (Oct 2025)</strong>.
          On the current post-clawback basis, AP's spend annualises to <strong>{fmt$(AP_RUN_RATE, 0)}</strong> —
          our annual contract price {fmt$(ann.annualPrice, 0)} is{' '}
          <strong>{((ann.annualPrice / AP_RUN_RATE) * 100).toFixed(1)}%</strong> of that run-rate
          ({((ann.annualPrice / AP_TRUE_GROSS) * 100).toFixed(1)}% of the full 52-week true gross).
          Overtime will be invoiced separately under the new contract structure.
        </div>
      </Card>
    </div>
  );
}

// ── Weekly Hours ──────────────────────────────────────────────────────────────
// Per-position weekly hours roster + the TCS Item D labour mapping (blended
// hourly rates so one rate-per-row tender forms reproduce the exact annual $).
function WeeklyHoursTab({ inp }) {
  const ann = calcAnnual(CHUTES, inp);
  const rows = [];
  CHUTES.forEach(c => {
    const k = calcChute(c, inp);
    const weeklyPer = k.sundayBillable + k.weekdayBillable * 4;
    const push = (role, heads, isTL) => heads > 0 && rows.push({
      chute: `${c.id} — ${c.name}`, role, heads, isTL,
      sun: k.sundayBillable, wkd: k.weekdayBillable, weeklyPer,
    });
    push('Team Leader', c.tl, true);
    push('Sorters', c.sorters, false);
    push('Runners', c.runners, false);
  });
  const totHeads  = rows.reduce((s, r) => s + r.heads, 0);
  const totWeekly = rows.reduce((s, r) => s + r.heads * r.weeklyPer, 0);

  // TCS Item D mapping — everyone works the same roster, so per-person annual
  // hours are identical; only the rates differ between TL and staff.
  const k005        = calcChute(CHUTES[0], inp);
  const weeklyHrs   = k005.sundayBillable + k005.weekdayBillable * 4;
  const annualHrs   = k005.sundayBillable * inp.sundayWeeks + k005.weekdayBillable * inp.weekdayWeeks;
  const tlAnnual$   = k005.sundayBillable * inp.sundayWeeks * inp.tlSunday + k005.weekdayBillable * inp.weekdayWeeks * inp.tlWeekday;
  const stAnnual$   = (k005.sundayBillable * inp.sundayWeeks * inp.sorterSunday + k005.weekdayBillable * inp.weekdayWeeks * inp.sorterWeekday) * 9;
  const gst         = ann.annualPrice * 0.1;

  const th = { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' };
  const td = { padding: '7px 12px', fontSize: 13.5, color: 'var(--hp-text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: '1px solid var(--hp-border)' };
  const left = { textAlign: 'left' };

  return (
    <div>
      <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>Weekly Hours by Position</div>
        <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginBottom: 14 }}>
          Sunday {k005.sundayBillable} h (23:15–06:15) + Mon–Thu 4 × {k005.weekdayBillable} h (23:45–06:15), breaks billed as per AP treatment.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Chute</th><th style={{ ...th, ...left }}>Position</th>
            <th style={th}>Heads</th><th style={th}>Sun hrs</th><th style={th}>Mon–Thu hrs</th>
            <th style={th}>Weekly hrs / person</th><th style={th}>Weekly hrs total</th><th style={th}>Annual hrs total</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ ...td, ...left }}>{r.chute}</td>
                <td style={{ ...td, ...left, fontWeight: r.isTL ? 800 : 500 }}>{r.role}</td>
                <td style={td}>{r.heads}</td>
                <td style={td}>{r.sun.toFixed(1)}</td>
                <td style={td}>{(r.wkd * 4).toFixed(1)}</td>
                <td style={td}>{r.weeklyPer.toFixed(1)}</td>
                <td style={td}>{(r.heads * r.weeklyPer).toFixed(1)}</td>
                <td style={td}>{(r.heads * (r.sun * inp.sundayWeeks + r.wkd * inp.weekdayWeeks)).toLocaleString()}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 800 }}>
              <td style={{ ...td, ...left }} colSpan={2}>Total</td>
              <td style={td}>{totHeads}</td>
              <td style={td}>{(rows.reduce((s, r) => s + r.heads * r.sun, 0)).toFixed(1)}</td>
              <td style={td}>{(rows.reduce((s, r) => s + r.heads * r.wkd * 4, 0)).toFixed(1)}</td>
              <td style={td}>—</td>
              <td style={td}>{totWeekly.toFixed(1)}</td>
              <td style={td}>{rows.reduce((s, r) => s + r.heads * (r.sun * inp.sundayWeeks + r.wkd * inp.weekdayWeeks), 0).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 20 }}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>TCS Item D — Labour Mapping</div>
        <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginBottom: 14 }}>
          For the AP Tender Cost Statement: one row per line with a blended hourly rate (Sunday + weekday mix) so the
          single-rate form reproduces the exact annual labour cost.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>TCS row</th><th style={th}>Hrs/wk</th><th style={th}>Weeks</th>
            <th style={th}>Annual hrs</th><th style={th}>Blended rate</th><th style={th}>Annual $</th>
          </tr></thead>
          <tbody>
            <tr>
              <td style={{ ...td, ...left }}>Contractor’s personnel — Team Leader (×1)</td>
              <td style={td}>{weeklyHrs.toFixed(1)}</td><td style={td}>{inp.sundayWeeks}</td>
              <td style={td}>{annualHrs.toLocaleString()}</td>
              <td style={td}>{'$' + (tlAnnual$ / annualHrs).toFixed(4)}</td>
              <td style={td}>{fmt$(tlAnnual$)}</td>
            </tr>
            <tr>
              <td style={{ ...td, ...left }}>Contractor’s personnel — Night sort staff (×9)</td>
              <td style={td}>{(weeklyHrs * 9).toFixed(1)}</td><td style={td}>{inp.sundayWeeks}</td>
              <td style={td}>{(annualHrs * 9).toLocaleString()}</td>
              <td style={td}>{'$' + (stAnnual$ / (annualHrs * 9)).toFixed(4)}</td>
              <td style={td}>{fmt$(stAnnual$)}</td>
            </tr>
            <tr><td style={{ ...td, ...left }}>Superannuation ({(inp.superRate * 100).toFixed(0)}%)</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualSuper)}</td></tr>
            <tr><td style={{ ...td, ...left }}>Workers’ compensation ({(inp.wcRate * 100).toFixed(1)}%)</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualWC)}</td></tr>
            <tr style={{ fontWeight: 700 }}><td style={{ ...td, ...left }}>Subtotal Labour (net)</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualTotal)}</td></tr>
            <tr><td style={{ ...td, ...left }}>Admin &amp; management ({(ann.adminPct * 100).toFixed(0)}%) — {ann.adminHoursPerWeek.toFixed(1)} h/wk</td><td style={td}>{ann.adminHoursPerWeek.toFixed(1)}</td><td style={td}>{ann.adminWeeks}</td><td style={td}>{ann.adminAnnualHours.toLocaleString()}</td><td style={td}>—</td><td style={td}>{fmt$(ann.adminFee)}</td></tr>
            <tr><td style={{ ...td, ...left }}>Profit ({(ann.profitPct * 100).toFixed(0)}%)</td><td style={td} colSpan={4} /><td style={{ ...td, color: ann.profit < 0 ? '#ef4444' : undefined }}>{fmt$(ann.profit)}</td></tr>
            <tr style={{ fontWeight: 700 }}><td style={{ ...td, ...left }}>Management loading ({(inp.markupPct * 100).toFixed(0)}%)</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualMarkup)}</td></tr>
            <tr style={{ fontWeight: 800 }}><td style={{ ...td, ...left }}>Total Net Value</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualPrice)}</td></tr>
            <tr><td style={{ ...td, ...left }}>GST (10%)</td><td style={td} colSpan={4} /><td style={td}>{fmt$(gst)}</td></tr>
            <tr style={{ fontWeight: 800 }}><td style={{ ...td, ...left }}>Total inc GST</td><td style={td} colSpan={4} /><td style={td}>{fmt$(ann.annualPrice + gst)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Labour Build-up ─────────────────────────────────────────────────────────
// Itemises the casual ordinary (base) rate and the loadings that make up each
// rate (Casual 25%, Shift 30%, Sunday 100% on the whole Sunday shift), then
// splits the annual cost across Sunday vs weekday hours. Costs use the page's
// rates so this ties out to the Weekly Hours / TCS Item D totals.
const PENALTY = { casual: 0.25, shift: 0.30, sunday: 1.00 };

function LabourBuildupTab({ inp }) {
  const k = calcChute(CHUTES[0], inp);
  const sunPerYr = k.sundayBillable * inp.sundayWeeks;     // per-person Sunday hrs/yr
  const wkdPerYr = k.weekdayBillable * inp.weekdayWeeks;   // per-person weekday hrs/yr (Mon–Thu)
  const roles = [
    { name: 'Contractor’s personnel — Team Leader (×1)', heads: 1, sun: inp.tlSunday, wkd: inp.tlWeekday },
    { name: 'Contractor’s personnel — Night sort staff (×9)', heads: 9, sun: inp.sorterSunday, wkd: inp.sorterWeekday },
  ];
  const built = roles.map(r => {
    const base = r.wkd / (1 + PENALTY.casual + PENALTY.shift);  // implied casual ordinary rate
    const casual$ = base * PENALTY.casual;
    const shift$ = base * PENALTY.shift;
    const sundayPen$ = r.sun - base - casual$;                  // balances to the actual Sunday rate
    const sunHrs = sunPerYr * r.heads, wkdHrs = wkdPerYr * r.heads;
    const sunCost = sunHrs * r.sun, wkdCost = wkdHrs * r.wkd;
    return { ...r, base, casual$, shift$, sundayPen$, sunHrs, wkdHrs, sunCost, wkdCost, total: sunCost + wkdCost };
  });
  const direct = built.reduce((s, b) => s + b.total, 0);
  const superC = direct * inp.superRate;
  const wc = direct * inp.wcRate;
  const net = direct + superC + wc;

  const th = { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' };
  const td = { padding: '7px 12px', fontSize: 13.5, color: 'var(--hp-text)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderTop: '1px solid var(--hp-border)' };
  const left = { textAlign: 'left' };
  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 20, marginBottom: 20 };

  return (
    <div>
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>Rate build-up (per hour)</div>
        <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginBottom: 14 }}>
          Weekday night = base + casual {(PENALTY.casual * 100).toFixed(0)}% + shift {(PENALTY.shift * 100).toFixed(0)}%.
          Sunday = base + casual {(PENALTY.casual * 100).toFixed(0)}% + Sunday {(PENALTY.sunday * 100).toFixed(0)}% (whole shift — the shift loading is replaced by the Sunday penalty). RDTA MA000038 casual.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Role / personnel</th>
            <th style={th}>Base ordinary</th><th style={th}>+ Casual {(PENALTY.casual * 100).toFixed(0)}%</th>
            <th style={th}>+ Shift {(PENALTY.shift * 100).toFixed(0)}%</th><th style={th}>Weekday night rate</th>
            <th style={th}>+ Sunday {(PENALTY.sunday * 100).toFixed(0)}%</th><th style={th}>Sunday rate</th>
          </tr></thead>
          <tbody>
            {built.map((b, i) => (
              <tr key={i}>
                <td style={{ ...td, ...left }}>{b.name}</td>
                <td style={td}>{fmt$(b.base)}</td>
                <td style={td}>{fmt$(b.casual$)}</td>
                <td style={td}>{fmt$(b.shift$)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt$(b.wkd)}</td>
                <td style={td}>{fmt$(b.sundayPen$)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt$(b.sun)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 2 }}>Annual cost — Sunday vs weekday hours</div>
        <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginBottom: 14 }}>
          Roster {k.sundayBillable} h Sun + 4 × {k.weekdayBillable} h Mon–Thu = {(k.sundayBillable + k.weekdayBillable * 4).toFixed(1)} h/wk/person × {inp.sundayWeeks} wks.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Role / personnel</th><th style={th}>People</th>
            <th style={th}>Sun hrs/yr</th><th style={th}>Wkd hrs/yr</th>
            <th style={th}>Sunday cost</th><th style={th}>Weekday cost</th><th style={th}>Annual labour</th>
          </tr></thead>
          <tbody>
            {built.map((b, i) => (
              <tr key={i}>
                <td style={{ ...td, ...left }}>{b.name}</td>
                <td style={td}>{b.heads}</td>
                <td style={td}>{b.sunHrs.toLocaleString()}</td>
                <td style={td}>{b.wkdHrs.toLocaleString()}</td>
                <td style={td}>{fmt$(b.sunCost)}</td>
                <td style={td}>{fmt$(b.wkdCost)}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt$(b.total)}</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 800 }}>
              <td style={{ ...td, ...left }} colSpan={6}>Total direct labour</td>
              <td style={td}>{fmt$(direct)}</td>
            </tr>
            <tr><td style={{ ...td, ...left }} colSpan={6}>Superannuation ({(inp.superRate * 100).toFixed(0)}%)</td><td style={td}>{fmt$(superC)}</td></tr>
            <tr><td style={{ ...td, ...left }} colSpan={6}>Workers’ compensation ({(inp.wcRate * 100).toFixed(1)}%)</td><td style={td}>{fmt$(wc)}</td></tr>
            <tr style={{ fontWeight: 800 }}><td style={{ ...td, ...left }} colSpan={6}>Subtotal Labour net value [ex GST]</td><td style={td}>{fmt$(net)}</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tender Tracker ──────────────────────────────────────────────────────────
// A living tracker for the AP night-sort tender — stage, next actions, timeline
// and confidential negotiation notes. Persisted to localStorage so it survives
// reloads. /night-sort is admin-only, so the internal notes stay private.
const TRACKER_KEY = 'hp-nightsort-tracker';
const STAGES = ['Pricing & analysis', 'Tender form submitted', 'Final meeting', 'Decision / award'];
const DEFAULT_TRACKER = {
  stageIdx: 2,
  actions: [
    { id: 'a1', text: 'Confirm final meeting date with Jamie Newport', due: '2026-06-18', done: false },
    { id: 'a2', text: 'Send AP the itemised loading breakdown (admin hrs + fee, operating expenses, profit) — replaces "contingency"', due: '', done: false },
    { id: 'a3', text: 'Follow up Jamie if no meeting date confirmed', due: '2026-06-19', done: false },
    { id: 'a4', text: 'After meeting: log the outcome & next steps here', due: '', done: false },
  ],
  timeline: [
    { id: 't1', date: '2026-06-14', note: 'Replied to Jamie with the completed TCS (both tabs) + answered the casual / contingency questions. Sent.' },
    { id: 't2', date: '2026-06-14', note: 'Itemised cost breakdown built — TCS "Pricing Template (itemised)" tab + app Labour Build-up.' },
    { id: 't3', date: '2026-06-11', note: 'Jamie email: transfer costing into AP tender form + 2 questions (casual? contingency?).' },
    { id: 't4', date: '2026-06-11', note: 'TCS pricing finalised — Total Net $1,041,677.60 (Labour $868,064.67 + 20% margin).' },
  ],
};

function TenderTrackerTab({ inp }) {
  const ann = calcAnnual(CHUTES, inp);
  const [tr, setTr] = useState(() => {
    try { const s = localStorage.getItem(TRACKER_KEY); return s ? { ...DEFAULT_TRACKER, ...JSON.parse(s) } : DEFAULT_TRACKER; }
    catch { return DEFAULT_TRACKER; }
  });
  useEffect(() => { try { localStorage.setItem(TRACKER_KEY, JSON.stringify(tr)); } catch {} }, [tr]);

  const setStage = (i) => setTr(p => ({ ...p, stageIdx: i }));
  const addAction = () => setTr(p => ({ ...p, actions: [...p.actions, { id: 'a' + Date.now(), text: '', due: '', done: false }] }));
  const updAction = (id, patch) => setTr(p => ({ ...p, actions: p.actions.map(a => a.id === id ? { ...a, ...patch } : a) }));
  const delAction = (id) => setTr(p => ({ ...p, actions: p.actions.filter(a => a.id !== id) }));
  const addEntry = () => setTr(p => ({ ...p, timeline: [{ id: 't' + Date.now(), date: new Date().toISOString().slice(0, 10), note: '' }, ...p.timeline] }));
  const updEntry = (id, patch) => setTr(p => ({ ...p, timeline: p.timeline.map(e => e.id === id ? { ...e, ...patch } : e) }));
  const delEntry = (id) => setTr(p => ({ ...p, timeline: p.timeline.filter(e => e.id !== id) }));

  const today = new Date().toISOString().slice(0, 10);
  const dueColor = (due, done) => {
    if (done || !due) return 'var(--hp-dim)';
    if (due < today) return 'var(--hp-red)';
    const soon = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
    return due <= soon ? 'var(--hp-amber)' : 'var(--hp-dim)';
  };

  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 20, marginBottom: 20 };
  const inputBare = { background: 'transparent', border: 'none', borderBottom: '1px solid var(--hp-border)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13.5, padding: '3px 2px', outline: 'none' };
  const sectionTitle = { fontWeight: 800, fontSize: 16, marginBottom: 14 };
  const sorted = [...tr.timeline].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const openActions = tr.actions.filter(a => !a.done).length;

  return (
    <div>
      {/* Stage stepper */}
      <div style={card}>
        <div style={{ ...sectionTitle, marginBottom: 12 }}>Tender stage</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {STAGES.map((s, i) => (
            <button key={s} onClick={() => setStage(i)} style={{
              padding: '8px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: i === tr.stageIdx ? 800 : 500,
              border: '1px solid ' + (i === tr.stageIdx ? 'var(--hp-gold)' : 'var(--hp-border)'),
              background: i === tr.stageIdx ? 'var(--hp-gold)' : (i < tr.stageIdx ? 'rgba(22,163,74,.12)' : 'var(--hp-surface)'),
              color: i === tr.stageIdx ? '#1a1a1a' : (i < tr.stageIdx ? 'var(--hp-green)' : 'var(--hp-dim)'),
            }}>
              {i < tr.stageIdx ? '✓ ' : ''}{s}
            </button>
          ))}
        </div>
      </div>

      {/* Key figures */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%),1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Net Value (ex GST)', val: fmt$(ann.annualPrice) },
          { label: 'Subtotal Labour', val: fmt$(ann.annualTotal) },
          { label: 'Margin', val: (inp.markupPct * 100).toFixed(0) + '%' },
          { label: 'AP true spend (ex GST)', val: fmt$(inp.apAnnualSpend) },
        ].map(k => (
          <div key={k.label} style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--hp-text)' }}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Next actions */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={sectionTitle}>Next actions {openActions > 0 && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--hp-gold)' }}>· {openActions} open</span>}</div>
          <button onClick={addAction} style={{ padding: '6px 12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'var(--font-body)' }}>+ Add</button>
        </div>
        {tr.actions.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid var(--hp-border)' }}>
            <input type="checkbox" checked={a.done} onChange={e => updAction(a.id, { done: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--hp-green)', flexShrink: 0 }} />
            <input value={a.text} onChange={e => updAction(a.id, { text: e.target.value })} placeholder="Action…" style={{ ...inputBare, flex: 1, textDecoration: a.done ? 'line-through' : 'none', color: a.done ? 'var(--hp-dim)' : 'var(--hp-text)' }} />
            <input type="date" value={a.due} onChange={e => updAction(a.id, { due: e.target.value })} style={{ ...inputBare, color: dueColor(a.due, a.done), width: 130 }} />
            <button onClick={() => delAction(a.id)} aria-label="Remove" style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 15, flexShrink: 0 }}>×</button>
          </div>
        ))}
      </div>

      {/* Timeline */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={sectionTitle}>Timeline</div>
          <button onClick={addEntry} style={{ padding: '6px 12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'var(--font-body)' }}>+ Add entry</button>
        </div>
        {sorted.map(e => (
          <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderTop: '1px solid var(--hp-border)' }}>
            <input type="date" value={e.date} onChange={ev => updEntry(e.id, { date: ev.target.value })} style={{ ...inputBare, width: 130, flexShrink: 0, color: 'var(--hp-gold)', fontWeight: 600 }} />
            <input value={e.note} onChange={ev => updEntry(e.id, { note: ev.target.value })} placeholder="What happened…" style={{ ...inputBare, flex: 1 }} />
            <button onClick={() => delEntry(e.id)} aria-label="Remove" style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 15, flexShrink: 0 }}>×</button>
          </div>
        ))}
      </div>

      {/* Confidential negotiation notes */}
      <div style={{ ...card, background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.3)' }}>
        <div style={{ ...sectionTitle, color: 'var(--hp-gold)' }}>Negotiation notes — internal only (do not share with AP)</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--hp-text)', lineHeight: 1.7 }}>
          <li>Loading now <strong>15% (10% admin + 5% profit)</strong> — opening 15% · compromise 13.5% · walk-away floor 12%. Don't go below 12%.</li>
          <li><strong>Admin headroom:</strong> demonstrable admin cost (management labour + systems) ≈ $56k (~6.5%); the 10% admin fee carries ≈ $30k of slack. That's the real concession room on top of the 5% profit — quietly available if AP pushes, never disclosed.</li>
          <li><strong>Contingency &amp; OT → PVA</strong> (Jamie's point): removed from the rate; bill ad-hoc cover / OT as PVAs only when they occur. Keeps the base price to the agreed spec.</li>
          <li><strong>Breaks (Jamie's point):</strong> we bill the Sunday 30-min + weekday 15-min as paid — this matches AP's OWN current schedule (7h Sun / 6.5h wkd). Hold the line; if AP insists on unpaid meal breaks that lowers billable hours (a further price drop), treat as a separate concession.</li>
          <li>Sunday = whole-shift 100% is <strong>AP's own treatment</strong>. Do <strong>not</strong> raise the award-strict (major-portion) position — that ≈ $66k/yr is private headroom, not a point to argue.</li>
          <li>Price now ≈ <strong>$998,274</strong> (15%) — about 98% of AP's post-clawback run-rate (~$1,016,636) and +13.6% on AP true spend ex GST ≈ $878,697.</li>
        </ul>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: '005', label: 'Chute 005 — City' },
  { id: '006', label: 'Chute 006 — Darlinghurst' },
  { id: '015', label: 'Chute 015 — Broadway' },
  { id: 'hours', label: 'Weekly Hours' },
  { id: 'buildup', label: 'Labour Build-up' },
  { id: 'tracker', label: 'Tender Tracker' },
  { id: 'letter', label: '✉ Proposal Letter' },
  { id: 'tcsletter', label: '✉ TCS Letter' },
  { id: 'desk', label: '🗂 Tender Desk' },
];

// ── Proposal Letter ───────────────────────────────────────────────────────────
function ProposalLetterTab({ inp }) {
  const ann = calcAnnual(CHUTES, inp);
  const [disclosure, setDisclosure] = useState('rationale'); // rationale | price | full
  const [recipient, setRecipient]   = useState('Australia Post — Procurement & Contracts');
  const [attn, setAttn]             = useState('');
  const pct   = (inp.markupPct * 100).toFixed(0);
  const money = n => '$' + Math.round(n).toLocaleString('en-AU');
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const BULLETS = [
    'On-site team leadership and live supervision on every shift;',
    'A technology stack that underpins accountability — Deputy (rostering and time-and-attendance), the Hollypaul Engine (operational tracking, shift verification and reporting), iComply (compliance and chain-of-responsibility) and Xero (payroll, Single Touch Payroll and financial controls);',
    'Recruitment, onboarding and turnover management for a hard-to-staff overnight workforce;',
    'Guaranteed absence cover so the sort is never left short-staffed;',
    'Full payroll, Single Touch Payroll and award-compliance administration; and',
    'Public liability and professional indemnity cover.',
  ];
  const TABLE = [
    ['Annual award labour', money(ann.annualLabour)],
    ['Superannuation (12%)', money(ann.annualSuper)],
    ["Workers' compensation (3.2%)", money(ann.annualWC)],
    ['Fully-loaded labour cost', money(ann.annualTotal)],
    [`Admin & management (${(ann.adminPct*100).toFixed(0)}%, ${ann.adminHoursPerWeek.toFixed(0)} hrs/wk)`, money(ann.adminFee)],
    [`Profit (${(ann.profitPct*100).toFixed(0)}%)`, money(ann.profit)],
    ['Total annual contract price', money(ann.annualPrice)],
  ];

  const paras = [
    `Thank you for the opportunity to submit our pricing for the permanent overnight sortation operation across the three Sydney CBD chutes — 005 (City), 006 (Darlinghurst) and 015 (Broadway). Our proposal covers a dedicated team of nine sorters and one on-site team leader, operating Sunday through Thursday nights.`,
    `Our price is built directly on the award. The base hourly rates and shift loadings are fixed by the Road Transport Distribution Award 2020 (MA000038) at Grade 1 (casual) and are non-negotiable. To these we add the two statutory on-costs every compliant employer must carry — superannuation (12%) and workers' compensation (3.2%). These are mandated pass-through costs, not margin.`,
    `The only discretionary element of our price is a management loading of ${pct}%. We want to be transparent about why we sit above a bare hourly rate: Hollypaul does not simply supply labour — we deliver a fully managed, supervised and audited operation that removes the compliance, payroll and labour-risk burden from Australia Post entirely. That loading funds a genuine operating infrastructure, including:`,
    '__BULLETS__',
    `Following your feedback we have reduced our loading to ${pct}% and broken it down as requested. A ${(ann.adminPct*100).toFixed(0)}% admin and management fee (${money(ann.adminFee)} per year) covers a dedicated management role of approximately ${ann.adminHoursPerWeek.toFixed(0)} hours per week — shift allocation and rostering, nightly shift confirmation, short-notice absence cover, pre-shift briefings, timesheet and payroll processing, recruitment, discipline and award-compliance administration — together with the operating systems behind it: Deputy (rostering and time-and-attendance), iComply (compliance), Xero (payroll and Single Touch Payroll) and IT. We have set this admin cost out as a separate, clearly identifiable item within the labour component so it is fully transparent.`,
    `The remaining ${(ann.profitPct*100).toFixed(0)}% (${money(ann.profit)} per year) is profit, in line with the guideline you noted. Variable, one-off costs — overtime and ad-hoc absence cover beyond the agreed roster — are deliberately not built into the rate; where they arise they would be raised as PVAs, so the contract price reflects the agreed specification only and you never pre-pay for cover that may not be needed.`,
    `For context, managed casual staffing in this market typically carries a loading of 20–35%, and specialist overnight operations — which are materially harder to staff and retain — sit at the 25–35% end. At ${pct}% all-in, with profit held to ${(ann.profitPct*100).toFixed(0)}%, our pricing sits well below that range and reflects the genuine cost of running a compliant, permanent night-sort team rather than an ad-hoc labour pool.`,
    ...(disclosure === 'price'
      ? [`On this basis, the total annual contract price is ${money(ann.annualPrice)}, inclusive of all award labour, statutory on-costs and the management loading.`]
      : disclosure === 'full'
      ? ['The build-up of the annual price is set out below:', '__TABLE__']
      : []),
    `We are confident this represents fair value for a low-risk, fully compliant and professionally managed operation, and we would welcome the opportunity to discuss it with you. We are committed to a long-term partnership and to delivering a reliable, accountable overnight sort for Australia Post.`,
  ];

  // Plain text (copy / email)
  const plainText = [
    today, '', `To: ${recipient}`, '',
    'Re: Night Sort Operations — Pricing Proposal (Sydney CBD)', '',
    `Dear ${attn || 'Sir/Madam'},`, '',
    ...paras.flatMap(p =>
      p === '__BULLETS__' ? BULLETS.map(b => '  • ' + b).concat('')
      : p === '__TABLE__' ? TABLE.map(([l, v]) => `  ${l}: ${v}`).concat('')
      : [p, '']),
    'Yours sincerely,', '', 'Paul Bouveng', 'Director', 'Hollypaul Recruitment Pty Ltd', 'paul@hollypaul.com',
  ].join('\n');

  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(plainText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }); };

  const printPDF = () => {
    const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const bodyHtml = paras.map(p => {
      if (p === '__BULLETS__') return `<ul>${BULLETS.map(b => `<li>${esc(b)}</li>`).join('')}</ul>`;
      if (p === '__TABLE__') return `<table class="bld">${TABLE.map(([l, v], i) => `<tr class="${i >= TABLE.length - 1 ? 'tot' : ''}"><td>${esc(l)}</td><td class="amt">${esc(v)}</td></tr>`).join('')}</table>`;
      return `<p>${esc(p)}</p>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Hollypaul — Night Sort Proposal</title>
<style>
  @page { margin: 0; }
  body { font-family: Helvetica, Arial, sans-serif; color:#1a1a1a; margin:0; font-size:11pt; line-height:1.5; }
  .hdr { background:#1a1a1a; padding:24px 28px 18px; }
  .hdr .brand { color:#c9a84c; font-weight:800; font-size:26pt; letter-spacing:1px; }
  .hdr .sub { color:#fff; font-weight:700; font-size:9pt; letter-spacing:2px; }
  .hdr .abn { color:#aaa; font-size:7.5pt; margin-top:8px; }
  .gold { height:3px; background:#c9a84c; }
  .body { padding:26px 34px; }
  .meta { color:#555; font-size:9.5pt; margin-bottom:14px; }
  .re { font-weight:800; margin:10px 0 16px; }
  p { margin:0 0 11px; }
  ul { margin:0 0 11px 18px; padding:0; } li { margin-bottom:5px; }
  table.bld { width:100%; border-collapse:collapse; margin:4px 0 12px; }
  table.bld td { padding:6px 0; border-bottom:1px solid #e5e5e5; font-size:10.5pt; }
  table.bld td.amt { text-align:right; font-variant-numeric:tabular-nums; }
  table.bld tr.tot td { font-weight:800; border-top:2px solid #1a1a1a; border-bottom:none; }
  .sig { margin-top:18px; } .sig .n { font-weight:800; }
  .foot { border-top:1px solid #ccc; margin:22px 34px 0; padding-top:8px; color:#777; font-size:7.5pt; display:flex; justify-content:space-between; }
</style></head><body>
  <div class="hdr"><div class="brand">HOLLYPAUL</div><div class="sub">RECRUITMENT PTY LTD</div>
    <div class="abn">ABN: 60 651 664 885&nbsp;&nbsp;|&nbsp;&nbsp;PO Box 1710, Warriewood NSW 2102&nbsp;&nbsp;|&nbsp;&nbsp;paul@hollypaul.com</div></div>
  <div class="gold"></div>
  <div class="body">
    <div class="meta">${esc(today)}<br>To: ${esc(recipient)}</div>
    <div class="re">Re: Night Sort Operations — Pricing Proposal (Sydney CBD)</div>
    <p>Dear ${esc(attn || 'Sir/Madam')},</p>
    ${bodyHtml}
    <div class="sig"><p style="margin-bottom:14px">Yours sincerely,</p>
      <div class="n">Paul Bouveng</div><div>Director</div><div>Hollypaul Recruitment Pty Ltd</div><div>paul@hollypaul.com</div></div>
  </div>
  <div class="foot"><span>Hollypaul Recruitment Pty Ltd&nbsp;&nbsp;|&nbsp;&nbsp;ABN 60 651 664 885</span><span>Commercial in confidence</span></div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const btn = (active) => ({
    padding: '7px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)',
    border: `1px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
    background: active ? 'rgba(201,168,76,0.15)' : 'var(--hp-surface)', color: active ? 'var(--hp-gold)' : 'var(--hp-dim)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <SectionTitle sub={`Generated live from this page — current loading ${pct}%. Set your opening loading on the Summary tab, then generate.`}>
          AP Proposal Letter
        </SectionTitle>

        {(() => {
          const wa = inp.negWalkaway ?? 0.16;
          const below = inp.markupPct < wa - 1e-9;
          return (
            <div style={{ background: below ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.08)', border: `1px solid ${below ? 'rgba(239,68,68,0.35)' : 'rgba(34,197,94,0.3)'}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--hp-text)', marginBottom: 16 }}>
              {below
                ? <>⚠ Loading is {pct}% — below your {(wa * 100).toFixed(0)}% walk-away minimum. Don't go under this floor.</>
                : <>This {pct}% loading covers our profit and the operational risks we retain — casual turnover, absence cover and administrative overhead. Australia Post carries any mandatory award-rate movements, so those are not loaded here. Keep some room to concede toward your {(wa * 100).toFixed(0)}% walk-away minimum.</>}
            </div>
          );
        })()}

        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 700 }}>Disclosure:</span>
            <button onClick={() => setDisclosure('rationale')} style={btn(disclosure === 'rationale')}>Rationale only</button>
            <button onClick={() => setDisclosure('price')} style={btn(disclosure === 'price')}>+ Annual price</button>
            <button onClick={() => setDisclosure('full')} style={btn(disclosure === 'full')}>Full breakdown</button>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--hp-dim)', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Addressed to
              <input value={recipient} onChange={e => setRecipient(e.target.value)} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--hp-dim)', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Attention (contact name — optional)
              <input value={attn} onChange={e => setAttn(e.target.value)} placeholder="e.g. Jane Smith" style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={printPDF} style={{ ...btn(false), background: 'var(--hp-gold)', color: '#1a1a1a', border: 'none' }}>Print / Save as PDF</button>
            <button onClick={copy} style={btn(false)}>{copied ? '✓ Copied' : 'Copy text (email)'}</button>
          </div>
        </div>

        {/* Live preview (mirrors the PDF) */}
        <div style={{ background: '#fff', color: '#1a1a1a', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--hp-border)' }}>
          <div style={{ background: '#1a1a1a', padding: '20px 24px 14px' }}>
            <div style={{ color: '#c9a84c', fontWeight: 800, fontSize: 24, letterSpacing: 1 }}>HOLLYPAUL</div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 10, letterSpacing: 2 }}>RECRUITMENT PTY LTD</div>
            <div style={{ color: '#aaa', fontSize: 9, marginTop: 6 }}>ABN: 60 651 664 885&nbsp;&nbsp;|&nbsp;&nbsp;PO Box 1710, Warriewood NSW 2102&nbsp;&nbsp;|&nbsp;&nbsp;paul@hollypaul.com</div>
          </div>
          <div style={{ height: 3, background: '#c9a84c' }} />
          <div style={{ padding: '24px 30px', fontSize: 13.5, lineHeight: 1.6 }}>
            <div style={{ color: '#555', fontSize: 12, marginBottom: 12 }}>{today}<br />To: {recipient}</div>
            <div style={{ fontWeight: 800, margin: '8px 0 14px' }}>Re: Night Sort Operations — Pricing Proposal (Sydney CBD)</div>
            <p style={{ margin: '0 0 11px' }}>Dear {attn || 'Sir/Madam'},</p>
            {paras.map((p, i) => {
              if (p === '__BULLETS__') return <ul key={i} style={{ margin: '0 0 11px 18px', padding: 0 }}>{BULLETS.map((b, j) => <li key={j} style={{ marginBottom: 5 }}>{b}</li>)}</ul>;
              if (p === '__TABLE__') return (
                <table key={i} style={{ width: '100%', borderCollapse: 'collapse', margin: '4px 0 12px' }}><tbody>
                  {TABLE.map(([l, v], j) => (
                    <tr key={j} style={j >= TABLE.length - 1 ? { fontWeight: 800, borderTop: '2px solid #1a1a1a' } : {}}>
                      <td style={{ padding: '6px 0', borderBottom: j >= TABLE.length - 1 ? 'none' : '1px solid #e5e5e5' }}>{l}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderBottom: j >= TABLE.length - 1 ? 'none' : '1px solid #e5e5e5' }}>{v}</td>
                    </tr>
                  ))}
                </tbody></table>
              );
              return <p key={i} style={{ margin: '0 0 11px' }}>{p}</p>;
            })}
            <div style={{ marginTop: 16 }}>
              <p style={{ margin: '0 0 14px' }}>Yours sincerely,</p>
              <div style={{ fontWeight: 800 }}>Paul Bouveng</div>
              <div>Director</div><div>Hollypaul Recruitment Pty Ltd</div><div>paul@hollypaul.com</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── TCS Letter (itemised cost-statement logic) ────────────────────────────────
function TCSLetterTab({ inp }) {
  const ann = calcAnnual(CHUTES, DEFAULT_INPUTS);  // locked to the submitted TCS basis (min award rates)
  const [mode, setMode]           = useState('full'); // logic | full
  const [recipient, setRecipient] = useState('Australia Post — Procurement & Contracts');
  const [attn, setAttn]           = useState('Jamie Newport');
  const money = n => '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  // Agreed TCS structure: award-min labour (live from the schedule) + statutory on-costs
  // + real admin (520 h/yr @ $70) + OPEX (own line) + a pure 5% profit on all expenses.
  const ADMIN = 10 * 52 * 70;            // 520 h/yr @ $70 = 36,400
  const SUBS  = 8356, BOOK = 6000, OPEX = SUBS + BOOK;   // 14,356
  const labourDirect = ann.annualLabour;
  const labourTotal  = labourDirect + ADMIN;             // admin sits inside labour, before on-costs
  const sRate = DEFAULT_INPUTS.superRate ?? 0.12, wRate = DEFAULT_INPUTS.wcRate ?? 0.032;
  const sup = labourTotal * sRate;                        // super on labour + admin
  const wc  = labourTotal * wRate;                        // workers' comp on labour + admin
  const labourBase = labourTotal + sup + wc;
  const expenses = labourBase + OPEX;
  const profit   = expenses * 0.05;
  const net      = expenses + profit;
  const gst      = net * 0.10;
  const inc      = net + gst;

  const BULLETS = [
    `Sort-floor labour — nine sorters and one team leader, priced at the award minimum: ${money(labourDirect)};`,
    `Management & administration — our principals, 520 hours a year (ten per week) at $70: ${money(ADMIN)}; and`,
    `Superannuation (12%) and workers' compensation (3.2%), applied to the combined labour: ${money(sup)} and ${money(wc)}.`,
  ];
  const TABLE = [
    ['Sort-floor labour (award minimum)', money(labourDirect), false],
    ['Management & admin — 520 h/yr @ $70', money(ADMIN), false],
    ['Total labour', money(labourTotal), true],
    ['Superannuation (12%)', money(sup), false],
    ["Workers' compensation (3.2%)", money(wc), false],
    ['Labour cost base', money(labourBase), true],
    ['Subscriptions (Deputy, Xero 25%, iComply, IT)', money(SUBS), false],
    ['Bookkeeping & accounting (25%)', money(BOOK), false],
    ['Total expenses', money(expenses), true],
    ['Profit (5%)', money(profit), false],
    ['Total Net Value (ex GST)', money(net), true],
    ['GST (10%)', money(gst), false],
    ['Total (inc GST)', money(inc), true],
  ];

  const paras = [
    `Thank you for the opportunity to revise our Tender Cost Statement. We have taken your feedback on board and reworked the pricing to be leaner and fully itemised; this letter sets out the logic behind the changes.`,
    `The award basis is unchanged — all labour is priced on the Road Transport Distribution Award 2020 (MA000038) at Grade 1 (casual). Our labour cost comprises the sort team and our own management time, with the statutory on-costs applied to the total:`,
    '__BULLETS__',
    `Fully-loaded labour is therefore ${money(labourBase)}.`,
    `Our management time — 520 hours a year at $70, ${money(ADMIN)} — covers shift allocation and rostering, nightly shift confirmation and short-notice absence cover, payroll and Single Touch Payroll, recruitment and onboarding, discipline, and award-compliance and WHS administration. It is real, documented work, which is why we include it within labour and apply superannuation and workers' compensation to it, as we would for any of our people.`,
    `The headline change from our original proposal is our margin: we have scaled it back significantly — from 20% to 5% — and itemised the operating costs we carry to run the contract, rather than bundling them into a loading.`,
    `Those operating costs are shown as their own line (${money(OPEX)}): software subscriptions of ${money(SUBS)} and external bookkeeping and accounting of ${money(BOOK)}. We have been careful to charge only a fair share — Xero has been reduced to 25% of its normal subscription, as that cost is shared with our contracting fleet; Deputy, which the fleet does not use, is charged exactly as subscribed; and bookkeeping and accounting has likewise been scaled to 25% of its normal cost, on an employee-versus-contractor basis.`,
    `Please note these rates are current and do not include the 4.75% modern-award increase that takes effect from 1 July 2026; that mandatory adjustment would apply on top, in line with the award.`,
    ...(mode === 'full'
      ? [`On this basis, our revised Tender Cost Statement totals ${money(net)} ex GST (${money(inc)} including GST). The full build-up is set out below:`, '__TABLE__']
      : [`On this basis, our revised Tender Cost Statement totals ${money(net)} ex GST (${money(inc)} including GST).`]),
    `We are confident this revised pricing represents fair, transparent and fully compliant value, and we would welcome the opportunity to discuss it with you. We remain committed to a reliable, accountable overnight sort for Australia Post.`,
  ];

  const plainText = [
    today, '', `To: ${recipient}`, '',
    'Re: Night Sort Operations — Revised Tender Cost Statement (Sydney CBD)', '',
    `Dear ${attn || 'Sir/Madam'},`, '',
    ...paras.flatMap(p =>
      p === '__BULLETS__' ? BULLETS.map(b => '  • ' + b).concat('')
      : p === '__TABLE__' ? TABLE.map(([l, v]) => `  ${l}: ${v}`).concat('')
      : [p, '']),
    'Yours sincerely,', '', 'Paul Bouveng', 'Director', 'PH Bouveng Pty Ltd', 'paul@hollypaul.com',
  ].join('\n');

  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(plainText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }); };

  const printPDF = () => {
    const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const bodyHtml = paras.map(p => {
      if (p === '__BULLETS__') return `<ul>${BULLETS.map(b => `<li>${esc(b)}</li>`).join('')}</ul>`;
      if (p === '__TABLE__') return `<table class="bld">${TABLE.map(([l, v, b]) => `<tr class="${b ? 'tot' : ''}"><td>${esc(l)}</td><td class="amt">${esc(v)}</td></tr>`).join('')}</table>`;
      return `<p>${esc(p)}</p>`;
    }).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>PH Bouveng — Night Sort TCS Letter</title>
<style>
  @page { margin: 0; }
  body { font-family: Helvetica, Arial, sans-serif; color:#1a1a1a; margin:0; font-size:11pt; line-height:1.5; }
  .hdr { background:#1a1a1a; padding:24px 28px 18px; }
  .hdr .brand { color:#c9a84c; font-weight:800; font-size:26pt; letter-spacing:1px; }
  .hdr .sub { color:#fff; font-weight:700; font-size:9pt; letter-spacing:2px; }
  .hdr .abn { color:#aaa; font-size:7.5pt; margin-top:8px; }
  .gold { height:3px; background:#c9a84c; }
  .body { padding:26px 34px; }
  .meta { color:#555; font-size:9.5pt; margin-bottom:14px; }
  .re { font-weight:800; margin:10px 0 16px; }
  p { margin:0 0 11px; }
  ul { margin:0 0 11px 18px; padding:0; } li { margin-bottom:5px; }
  table.bld { width:100%; border-collapse:collapse; margin:4px 0 12px; }
  table.bld td { padding:6px 0; border-bottom:1px solid #e5e5e5; font-size:10.5pt; }
  table.bld td.amt { text-align:right; font-variant-numeric:tabular-nums; }
  table.bld tr.tot td { font-weight:800; border-top:2px solid #1a1a1a; }
  .sig { margin-top:18px; } .sig .n { font-weight:800; }
  .foot { border-top:1px solid #ccc; margin:22px 34px 0; padding-top:8px; color:#777; font-size:7.5pt; display:flex; justify-content:space-between; }
</style></head><body>
  <div class="hdr"><div class="brand">PH BOUVENG</div><div class="sub">PTY LTD</div>
    <div class="abn">ABN: 12 093 039 297&nbsp;&nbsp;|&nbsp;&nbsp;PO Box 1710, Warriewood NSW 2102&nbsp;&nbsp;|&nbsp;&nbsp;paul@hollypaul.com</div></div>
  <div class="gold"></div>
  <div class="body">
    <div class="meta">${esc(today)}<br>To: ${esc(recipient)}</div>
    <div class="re">Re: Night Sort Operations — Revised Tender Cost Statement (Sydney CBD)</div>
    <p>Dear ${esc(attn || 'Sir/Madam')},</p>
    ${bodyHtml}
    <div class="sig"><p style="margin-bottom:14px">Yours sincerely,</p>
      <div class="n">Paul Bouveng</div><div>Director</div><div>PH Bouveng Pty Ltd</div><div>paul@hollypaul.com</div></div>
  </div>
  <div class="foot"><span>PH Bouveng Pty Ltd&nbsp;&nbsp;|&nbsp;&nbsp;ABN 12 093 039 297</span><span>Commercial in confidence</span></div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const btn = (active) => ({
    padding: '7px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)',
    border: `1px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
    background: active ? 'rgba(201,168,76,0.15)' : 'var(--hp-surface)', color: active ? 'var(--hp-gold)' : 'var(--hp-dim)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <SectionTitle sub="A revision letter for Jamie — thanks him for the chance to revise, then explains the leaner, itemised TCS (margin cut from 20% to 5%). Figures match the submitted TCS spreadsheet.">
          AP Revised TCS Letter
        </SectionTitle>

        <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--hp-text)', marginBottom: 16 }}>
          Revised TCS — admin sits inside labour (super &amp; WC applied to the total); margin scaled back from 20% to 5%; OPEX itemised. → <strong>{money(net)}</strong> ex GST ({money(inc)} inc GST).
        </div>

        <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 700 }}>Detail:</span>
            <button onClick={() => setMode('logic')} style={btn(mode === 'logic')}>Logic only</button>
            <button onClick={() => setMode('full')} style={btn(mode === 'full')}>Logic + cost statement</button>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--hp-dim)', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Addressed to
              <input value={recipient} onChange={e => setRecipient(e.target.value)} style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13 }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--hp-dim)', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
              Attention (contact name — optional)
              <input value={attn} onChange={e => setAttn(e.target.value)} placeholder="e.g. Jamie Newport" style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13 }} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={printPDF} style={{ ...btn(false), background: 'var(--hp-gold)', color: '#1a1a1a', border: 'none' }}>Print / Save as PDF</button>
            <button onClick={copy} style={btn(false)}>{copied ? '✓ Copied' : 'Copy text (email)'}</button>
          </div>
        </div>

        {/* Live preview (mirrors the PDF) */}
        <div style={{ background: '#fff', color: '#1a1a1a', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--hp-border)' }}>
          <div style={{ background: '#1a1a1a', padding: '20px 24px 14px' }}>
            <div style={{ color: '#c9a84c', fontWeight: 800, fontSize: 24, letterSpacing: 1 }}>PH BOUVENG</div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 10, letterSpacing: 2 }}>PTY LTD</div>
            <div style={{ color: '#aaa', fontSize: 9, marginTop: 6 }}>ABN: 12 093 039 297&nbsp;&nbsp;|&nbsp;&nbsp;PO Box 1710, Warriewood NSW 2102&nbsp;&nbsp;|&nbsp;&nbsp;paul@hollypaul.com</div>
          </div>
          <div style={{ height: 3, background: '#c9a84c' }} />
          <div style={{ padding: '24px 30px', fontSize: 13.5, lineHeight: 1.6 }}>
            <div style={{ color: '#555', fontSize: 12, marginBottom: 12 }}>{today}<br />To: {recipient}</div>
            <div style={{ fontWeight: 800, margin: '8px 0 14px' }}>Re: Night Sort Operations — Revised Tender Cost Statement (Sydney CBD)</div>
            <p style={{ margin: '0 0 11px' }}>Dear {attn || 'Sir/Madam'},</p>
            {paras.map((p, i) => {
              if (p === '__BULLETS__') return <ul key={i} style={{ margin: '0 0 11px 18px', padding: 0 }}>{BULLETS.map((b, j) => <li key={j} style={{ marginBottom: 5 }}>{b}</li>)}</ul>;
              if (p === '__TABLE__') return (
                <table key={i} style={{ width: '100%', borderCollapse: 'collapse', margin: '4px 0 12px' }}><tbody>
                  {TABLE.map(([l, v, b], j) => (
                    <tr key={j} style={b ? { fontWeight: 800, borderTop: '2px solid #1a1a1a' } : {}}>
                      <td style={{ padding: '6px 0', borderBottom: '1px solid #e5e5e5' }}>{l}</td>
                      <td style={{ padding: '6px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid #e5e5e5' }}>{v}</td>
                    </tr>
                  ))}
                </tbody></table>
              );
              return <p key={i} style={{ margin: '0 0 11px' }}>{p}</p>;
            })}
            <div style={{ marginTop: 16 }}>
              <p style={{ margin: '0 0 14px' }}>Yours sincerely,</p>
              <div style={{ fontWeight: 800 }}>Paul Bouveng</div>
              <div>Director</div><div>PH Bouveng Pty Ltd</div><div>paul@hollypaul.com</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Tender Desk (brief to Claude · AP correspondence · TCS documents) ─────────
const readAsBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = reject;
  r.readAsDataURL(blob);
});

// ── Faithful xlsx → HTML (preserves fills, fonts, number formats, merges) ──────
const colToNum = (s) => { let n = 0; for (let i = 0; i < s.length; i++) n = n * 26 + (s.charCodeAt(i) - 64); return n; };
const argbToHex = (argb) => { if (!argb || typeof argb !== 'string') return null; return '#' + (argb.length === 8 ? argb.slice(2) : argb); };
const escHtml = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function xlCellText(cell) {
  let v = cell.value;
  if (v == null) return '';
  if (typeof v === 'object') {
    if (v.richText) v = v.richText.map(t => t.text).join('');
    else if (v.result != null) v = v.result;
    else if (v.text != null) v = v.text;
    else if (v instanceof Date) return v.toLocaleDateString('en-AU');
    else v = '';
  }
  const fmt = cell.numFmt || '';
  if (typeof v === 'number') {
    if (/0\.00/.test(fmt)) return v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (/%/.test(fmt)) return (v * 100).toFixed(/0\.0/.test(fmt) ? 1 : 0) + '%';
    if (/[#0],/.test(fmt)) return v.toLocaleString('en-AU');
    return String(v);
  }
  return String(v);
}
function renderExcelSheet(ws) {
  const merges = (ws.model && ws.model.merges) || [];
  const covered = {}, spans = {};
  merges.forEach(rng => {
    const m = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(rng); if (!m) return;
    const c1 = colToNum(m[1]), r1 = +m[2], c2 = colToNum(m[3]), r2 = +m[4];
    spans[r1 + ',' + c1] = { rs: r2 - r1 + 1, cs: c2 - c1 + 1 };
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) if (!(r === r1 && c === c1)) covered[r + ',' + c] = true;
  });
  const rows = ws.actualRowCount || ws.rowCount || 0;
  let cols = Math.min(ws.actualColumnCount || ws.columnCount || 8, 14);
  let h = '<table>';
  for (let r = 1; r <= rows; r++) {
    const row = ws.getRow(r);
    h += '<tr>';
    for (let c = 1; c <= cols; c++) {
      if (covered[r + ',' + c]) continue;
      const cell = row.getCell(c);
      const sp = spans[r + ',' + c];
      const st = [];
      const fill = cell.fill;
      if (fill && fill.type === 'pattern' && fill.fgColor && fill.fgColor.argb) {
        const bg = argbToHex(fill.fgColor.argb);
        if (bg && bg.toUpperCase() !== '#FFFFFF') st.push('background:' + bg);
      }
      const f = cell.font || {};
      if (f.bold) st.push('font-weight:700');
      if (f.italic) st.push('font-style:italic');
      if (f.color && f.color.argb) { const fc = argbToHex(f.color.argb); if (fc && fc.toUpperCase() !== '#FF000000') st.push('color:' + fc); }
      const al = cell.alignment || {};
      if (al.horizontal) st.push('text-align:' + al.horizontal);
      const spanAttr = sp ? ` colspan="${sp.cs}" rowspan="${sp.rs}"` : '';
      h += `<td${spanAttr} style="${st.join(';')}">${escHtml(xlCellText(cell))}</td>`;
    }
    h += '</tr>';
  }
  return h + '</table>';
}

function TenderDeskTab() {
  const app = useApp();
  const me = (app?.currentUser?.login || '').toUpperCase();
  const notify = (m, t) => app?.addNotification ? app.addNotification(m, t) : null;
  const callAS = async (action, payload) => {
    const res = await fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...payload }),
    });
    return res.json();
  };

  const [brief, setBrief] = useState('');
  const [briefAt, setBriefAt] = useState('');
  const [briefSaving, setBriefSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState('');
  const [viewer, setViewer] = useState(null);
  const [viewerLoading, setViewerLoading] = useState('');

  const refreshFiles = () => {
    setFilesLoading(true);
    callAS('nsListFiles', { category: 'correspondence' })
      .then(d => { if (d.success) setFiles(d.files || []); })
      .catch(() => {}).finally(() => setFilesLoading(false));
  };
  useEffect(() => {
    callAS('nsGetBrief', {}).then(d => { if (d.success) { setBrief(d.text || ''); setBriefAt(d.updatedAt || ''); } }).catch(() => {});
    refreshFiles();
  }, []); // eslint-disable-line

  const saveBrief = async () => {
    setBriefSaving(true);
    const d = await callAS('nsSaveBrief', { text: brief, by: me });
    setBriefSaving(false);
    if (d.success) { setBriefAt(d.updatedAt); notify('Brief saved — Claude will read it next session', 'success'); }
    else notify('Could not save brief', 'error');
  };

  const uploadFiles = async (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setUploading(true);
    for (const f of arr) {
      if (f.size > 12 * 1024 * 1024) { notify(`${f.name} is over 12 MB — skipped`, 'error'); continue; }
      try {
        const base64 = await readAsBase64(f);
        const d = await callAS('nsSaveFile', { category: 'correspondence', filename: f.name, contentType: f.type || 'application/octet-stream', base64, by: me });
        if (!d.success) notify(`Upload failed: ${f.name}`, 'error');
      } catch { notify(`Upload failed: ${f.name}`, 'error'); }
    }
    setUploading(false);
    refreshFiles();
    notify('Correspondence uploaded', 'success');
  };

  const delFile = async (fileId) => {
    setFiles(prev => prev.filter(f => f.fileId !== fileId));
    await callAS('nsDeleteFile', { fileId });
  };

  const sendTCS = async (which) => {
    const to = sendTo.trim();
    if (!to || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { notify('Enter a valid recipient email first', 'error'); return; }
    setSending(which);
    try {
      const url = which === 'completed' ? '/docs/night-sort-tcs-completed.xlsx' : '/docs/night-sort-tcs-blank.xlsx';
      const blob = await (await fetch(url)).blob();
      const base64 = await readAsBase64(blob);
      const res = await fetch('/.netlify/functions/tcs-send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, which, base64, by: me }),
      });
      const d = await res.json();
      if (d.success) notify(`TCS sent to ${to}`, 'success');
      else notify('Send failed: ' + (d.error || ''), 'error');
    } catch (e) { notify('Send failed: ' + e.message, 'error'); }
    setSending('');
  };

  const viewTCS = async (row) => {
    setViewerLoading(row.which);
    try {
      const buf = await (await fetch(row.href)).arrayBuffer();
      let names = [];
      const htmls = {};
      try {
        // ExcelJS browser build (lazy) — preserves fills, fonts, currency, merges
        const mod = await import('exceljs/dist/exceljs.min.js');
        const ExcelJS = (mod.default && mod.default.Workbook) ? mod.default : (mod.Workbook ? mod : (mod.default || mod));
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        names = wb.worksheets.map(w => w.name);
        wb.worksheets.forEach(w => { htmls[w.name] = renderExcelSheet(w); });
      } catch (eStyled) {
        // Fallback: SheetJS (values only, no styling)
        const wb = XLSX.read(buf, { type: 'array' });
        names = wb.SheetNames;
        names.forEach(n => { htmls[n] = XLSX.utils.sheet_to_html(wb.Sheets[n]); });
      }
      const active = names.find(n => /itemised/i.test(n)) || names[names.length - 1] || names[0];
      setViewer({ filename: row.title, href: row.href, file: row.file, names, htmls, active });
    } catch (e) { notify('Could not open file: ' + e.message, 'error'); }
    setViewerLoading('');
  };

  const goldBtn = { padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', background: 'var(--hp-gold)', color: '#1a1a1a', border: 'none' };
  const ghostBtn = { padding: '8px 16px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-body)', background: 'var(--hp-surface)', color: 'var(--hp-text)', border: '1px solid var(--hp-border)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 };
  const fmtAt = iso => { const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || ''); return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : ''; };

  const TCS_ROWS = [
    { which: 'blank', title: 'TCS — Blank template', sub: 'AP Pricing Template (unpopulated)', href: '/docs/night-sort-tcs-blank.xlsx', file: 'Night Sort TCS 2026 - blank.xlsx' },
    { which: 'completed', title: 'TCS — Completed (PH Bouveng Pty Ltd)', sub: 'Itemised cost statement · $970,571.14 ex GST', href: '/docs/night-sort-tcs-completed.xlsx', file: 'Night Sort TCS 2026 - PH Bouveng Pty Ltd.xlsx' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Brief to Claude */}
      <Card>
        <SectionTitle sub="Tell Claude what's required and where we need to get to. Saved to the project — Claude Code reads it here and actions it. (A live in-page chat is planned for later.)">
          Brief to Claude
        </SectionTitle>
        <textarea
          value={brief} onChange={e => setBrief(e.target.value)}
          placeholder="e.g. Jamie wants the Sunday break treated as unpaid — recalc and re-issue the TCS; aim to land under $950k…"
          style={{ width: '100%', minHeight: 120, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
          <button style={{ ...goldBtn, opacity: briefSaving ? 0.6 : 1 }} onClick={saveBrief} disabled={briefSaving}>{briefSaving ? 'Saving…' : 'Save brief'}</button>
          {briefAt && <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>Last saved {fmtAt(briefAt)}</span>}
        </div>
      </Card>

      {/* AP Correspondence */}
      <Card>
        <SectionTitle sub="Upload anything received from Australia Post (PDF, email, image). Stored securely; open or download it any time.">
          AP Correspondence
        </SectionTitle>
        <label
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
          style={{ display: 'block', border: `2px dashed ${drag ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: '22px', textAlign: 'center', cursor: 'pointer', background: drag ? 'rgba(201,168,76,0.08)' : 'var(--hp-bg)', marginBottom: 14 }}>
          <input type="file" multiple style={{ display: 'none' }} onChange={e => uploadFiles(e.target.files)} />
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hp-text)' }}>{uploading ? 'Uploading…' : 'Drop files here or click to upload'}</div>
          <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 4 }}>PDF, email or image · up to 12 MB each</div>
        </label>
        {filesLoading ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: 13 }}>Loading…</div>
        ) : files.length === 0 ? (
          <div style={{ color: 'var(--hp-dim)', fontSize: 13, textAlign: 'center', padding: '8px 0' }}>No correspondence uploaded yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map(f => (
              <div key={f.fileId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                <span style={{ flex: 1, fontSize: 13, color: 'var(--hp-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
                <span style={{ fontSize: 11, color: 'var(--hp-dim)', whiteSpace: 'nowrap' }}>{fmtAt(f.uploadedAt)}</span>
                <a href={f.viewUrl} target="_blank" rel="noreferrer" style={{ ...ghostBtn, padding: '5px 10px', fontSize: 12 }}>View</a>
                <a href={f.downloadUrl} target="_blank" rel="noreferrer" style={{ ...ghostBtn, padding: '5px 10px', fontSize: 12 }}>Download</a>
                <button onClick={() => delFile(f.fileId)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 4, fontSize: 14 }}>✕</button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* TCS Documents */}
      <Card>
        <SectionTitle sub="Both tender cost statements, ready to download or email to Australia Post.">
          TCS Documents
        </SectionTitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 700 }}>Send to:</span>
          <input value={sendTo} onChange={e => setSendTo(e.target.value)} placeholder="jamie.newport@auspost.com.au"
            style={{ flex: 1, minWidth: 240, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontFamily: 'var(--font-body)', fontSize: 13 }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {TCS_ROWS.map(row => (
            <div key={row.which} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--hp-border)', borderRadius: 8, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hp-text)' }}>{row.title}</div>
                <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>{row.sub}</div>
              </div>
              <button style={{ ...ghostBtn, opacity: viewerLoading === row.which ? 0.6 : 1 }} onClick={() => viewTCS(row)} disabled={!!viewerLoading}>
                {viewerLoading === row.which ? 'Opening…' : '👁 View'}
              </button>
              <a href={row.href} download={row.file} style={ghostBtn}>↓ Download</a>
              <button style={{ ...goldBtn, opacity: sending === row.which ? 0.6 : 1 }} onClick={() => sendTCS(row.which)} disabled={!!sending}>
                {sending === row.which ? 'Sending…' : '✉ Send'}
              </button>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 10 }}>The completed TCS updates whenever we revise the pricing. Send emails the file to the address above (a copy goes to paul@hollypaul.com).</div>
      </Card>

      {/* TCS viewer modal */}
      {viewer && (
        <div onClick={() => setViewer(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', color: '#1a1a1a', borderRadius: 10, width: 'min(1100px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #e5e5e5' }}>
              <div style={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{viewer.filename}</div>
              <a href={viewer.href} download={viewer.file} style={{ ...ghostBtn, padding: '6px 12px', fontSize: 12 }}>↓ Download</a>
              <button onClick={() => setViewer(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#666', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 6, padding: '10px 18px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
              {viewer.names.map(n => (
                <button key={n} onClick={() => setViewer(v => ({ ...v, active: n }))} style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: '1px solid ' + (viewer.active === n ? '#c9a84c' : '#ddd'), background: viewer.active === n ? 'rgba(201,168,76,0.15)' : '#fff', color: viewer.active === n ? '#9a7b1f' : '#555' }}>{n}</button>
              ))}
            </div>
            <div style={{ overflow: 'auto', padding: '14px 18px', flex: 1 }}>
              <style>{`.tcs-view table{border-collapse:collapse;font-size:12px;font-family:Helvetica,Arial,sans-serif} .tcs-view td{border:1px solid #e8e8e8;padding:4px 9px;vertical-align:top}`}</style>
              <div className="tcs-view" dangerouslySetInnerHTML={{ __html: viewer.htmls[viewer.active] || '' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function NightSortPage() {
  const [activeTab, setActiveTab] = useState('summary');
  const [inp, setInp] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
      // Migration: the pre-invoice-audit AP spend figure ($1,083,435.78) was
      // wrong — replace a saved copy of it with the true invoice-based figure.
      if (Math.round(stored.apAnnualSpend) === 1083436) stored.apAnnualSpend = DEFAULT_INPUTS.apAnnualSpend;
      // Migration: move pre-split pricing (single 20% loading) onto the 15% model
      // (10% admin + 5% profit), refreshed negotiation band and admin task list.
      if (stored.profitPct === undefined) {
        stored.profitPct    = DEFAULT_INPUTS.profitPct;
        stored.markupPct    = DEFAULT_INPUTS.markupPct;
        stored.negOpening   = DEFAULT_INPUTS.negOpening;
        stored.negCompromise = DEFAULT_INPUTS.negCompromise;
        stored.negWalkaway  = DEFAULT_INPUTS.negWalkaway;
        stored.adminTasks   = DEFAULT_INPUTS.adminTasks;
      }
      return { ...DEFAULT_INPUTS, ...stored };
    } catch {
      return DEFAULT_INPUTS;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(inp));
  }, [inp]);

  function handleChange(key, val) {
    setInp(prev => ({ ...prev, [key]: val }));
  }

  function handleReset() {
    if (window.confirm('Reset all rates and settings to AP defaults?')) {
      setInp(DEFAULT_INPUTS);
    }
  }

  const activeChute = CHUTES.find(c => c.id === activeTab);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontWeight: 900, fontSize: 22, color: 'var(--hp-text)' }}>
              Night Sort — Contract Pricing
            </h1>
            <div style={{ color: 'var(--hp-dim)', fontSize: 13, marginTop: 4 }}>
              Sydney CBD · RDTA MA000038 · 9 Sorters + 1 Team Leader · Sun–Thu nights
            </div>
          </div>
          <button onClick={handleReset} style={{ padding: '8px 16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}>
            Reset to AP defaults
          </button>
        </div>
      </div>

      {/* Rates & Break inputs — always visible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
        <RatesCard inp={inp} onChange={handleChange} />
        <BreakAnalysisCard inp={inp} onChange={handleChange} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid var(--hp-border)', marginBottom: 20, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: activeTab === t.id ? 800 : 500,
            color: activeTab === t.id ? 'var(--hp-gold)' : 'var(--hp-dim)',
            borderBottom: activeTab === t.id ? '2px solid var(--hp-gold)' : '2px solid transparent',
            marginBottom: -2, whiteSpace: 'nowrap',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'summary' && <SummaryTab inp={inp} onChangeInp={handleChange} />}
      {activeTab === 'hours' && <WeeklyHoursTab inp={inp} />}
      {activeTab === 'buildup' && <LabourBuildupTab inp={inp} />}
      {activeTab === 'tracker' && <TenderTrackerTab inp={inp} />}
      {activeTab === 'letter' && <ProposalLetterTab inp={inp} />}
      {activeTab === 'tcsletter' && <TCSLetterTab inp={inp} />}
      {activeTab === 'desk' && <TenderDeskTab />}
      {activeChute && <ChuteTab chute={activeChute} inp={inp} />}
    </div>
  );
}
